import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Headphones,
  Send,
  RefreshCw,
  Clock,
  Tag,
  PlusCircle,
  ArrowLeft,
  Inbox,
  Search,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  User,
  FileText,
  Paperclip,
  X,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import BackgroundFooter from "./BackgroundFooter";
import { Spinner } from "react-bootstrap";

// ── Region resolver (shared) ──
// Region is read from sessionStorage; falls back to GB to preserve existing behaviour.
const getUserRegion = () => sessionStorage.getItem("userRegion") || "GB";

// Resolve a base URL for a given per-function map + current region.
const resolveBaseUrl = (apiMap, region = getUserRegion()) => {
  const base = apiMap[region];
  if (!base)
    throw new Error(`No API endpoint configured for region: ${region}`);
  return base;
};

// ── Auth ──
// CONFIRM THIS KEY. Change it to whatever your login flow writes the Cognito
// ID token under. If it is wrong every request 401s and the cause is not obvious.
const ID_TOKEN_KEY = "idToken";

// Read on every call, never cached in a module variable. Cognito ID tokens
// expire after an hour by default, so a value captured once at app start goes
// stale mid-session and the user sees an unexplained 401 while typing a reply.
const getIdToken = () => sessionStorage.getItem(ID_TOKEN_KEY) || "";

/**
 * Dedicated axios instance for helpdesk calls.
 *
 * A separate instance rather than mutating axios defaults, so these headers
 * cannot leak into other components' requests. The presigned S3 PUT below
 * deliberately uses raw fetch and is unaffected either way: an Authorization
 * header on a presigned request breaks the signature.
 */
const api = axios.create();

api.interceptors.request.use((config) => {
  const token = getIdToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers.Region = getUserRegion();
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // TODO(auth): call your auth library's refresh here, then retry once.
      // With Amplify this is fetchAuthSession({ forceRefresh: true }), writing
      // the new token back to sessionStorage before replaying the request.
      // Until that is wired, an expired token surfaces as a generic failure.
      console.warn("Helpdesk request rejected: token missing or expired");
    }
    return Promise.reject(error);
  },
);

// ── One regional map PER backend function ──
// GB is filled with the current live host. Fill IN / US / AE with each
// function's real regional gateway hostname.
const GET_TICKETS_BY_REQUESTER_API = {
  IN: "https://5uwit51g90.execute-api.ap-south-1.amazonaws.com/prod",
  GB: "https://pio9n5ym63.execute-api.eu-west-1.amazonaws.com/prod",
  US: "https://c0medxl0sk.execute-api.us-west-2.amazonaws.com/prod",
  AE: "https://YOUR-AE-GET-TICKETS-BY-REQUESTER-API",
};

const GET_TICKET_BY_ID_API = {
  IN: "https://bn4kq4hnif.execute-api.ap-south-1.amazonaws.com/prod",
  GB: "https://omz3uk0uu4.execute-api.eu-west-1.amazonaws.com/prod",
  US: "https://q85xkmyu4k.execute-api.us-west-2.amazonaws.com/prod",
  AE: "https://YOUR-AE-GET-TICKET-BY-ID-API",
};

const CREATE_TICKET_API = {
  IN: "https://lq4868l8g8.execute-api.ap-south-1.amazonaws.com/prod",
  GB: "https://ovi4qht554.execute-api.eu-west-1.amazonaws.com/prod",
  US: "https://edsdofk232.execute-api.us-west-2.amazonaws.com/prod",
  AE: "https://YOUR-AE-CREATE-TICKET-API",
};

const ADD_REPLY_API = {
  IN: "https://td32rsjt9j.execute-api.ap-south-1.amazonaws.com/prod",
  GB: "https://z4eumjz8u3.execute-api.eu-west-1.amazonaws.com/prod",
  US: "https://fac3fldvm5.execute-api.us-west-2.amazonaws.com/prod",
  AE: "https://YOUR-AE-ADD-REPLY-API",
};

const ATTACHMENT_INTENT_API = {
  IN: "https://ikxd2hks4c.execute-api.ap-south-1.amazonaws.com/Prod",
  GB: "https://oll5r0w2w5.execute-api.eu-west-1.amazonaws.com/prod",
  US: "https://mdtqhli9v8.execute-api.us-west-2.amazonaws.com/Prod",
  AE: "https://YOUR-AE-ATTACHMENT-INTENT-API",
};

const ATTACHMENT_DOWNLOAD_API = {
  IN: "https://fzv4qitevi.execute-api.ap-south-1.amazonaws.com/prod",
  GB: "https://76yhprq8l1.execute-api.eu-west-1.amazonaws.com/prod",
  US: "https://fyt79p593b.execute-api.us-west-2.amazonaws.com/prod",
  AE: "https://YOUR-AE-ATTACHMENT-DOWNLOAD-API",
};

const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
const ATTACH_TYPES = {
  "application/pdf": ["pdf"],
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
};
const MAX_TICKET_ATTACHMENTS = 10;

// Convenience only. The server revalidates type and size, and re-reads the
// real values from S3 object metadata, so bypassing this achieves nothing.
function validateAttachment(f) {
  if (!f) return "";
  if (f.size > ATTACH_MAX_BYTES) return "File size exceeds 10 MB.";
  if (f.size === 0) return "File appears to be empty.";
  const allowed = ATTACH_TYPES[f.type];
  if (!allowed) return "Only PDF, PNG and JPG files are supported.";
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  if (!allowed.includes(ext)) return "File extension does not match its type.";
  return "";
}

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Two-step upload: ask the backend for a presigned PUT, then send the bytes
 * straight to S3. Returns { s3Key } to attach to the ticket/reply payload.
 *
 * The S3 PUT uses raw fetch on purpose. An Authorization header on a presigned
 * request breaks the signature, so this must never be converted to the api
 * instance above.
 */
async function uploadAttachment(file, ticketId) {
  const base = resolveBaseUrl(ATTACHMENT_INTENT_API);

  const intentRes = await api.post(
    `${base}/api/helpdesk/tickets/customer/attachment-intent`,
    {
      ticketId: ticketId || null,
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    },
    { headers: { "Content-Type": "application/json" } },
  );

  if (!intentRes.data?.success || !intentRes.data.data?.uploadUrl) {
    throw new Error(intentRes.data?.message || "Could not prepare the upload.");
  }

  const { uploadUrl, s3Key, contentType } = intentRes.data.data;

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  });
  if (!putRes.ok) throw new Error("Upload to storage failed.");

  return { s3Key };
}

const PRIORITIES = [
  {
    value: "P1",
    label: "P1 - Critical",
    color: "#dc3545",
    bg: "#fff0f0",
    description: "Complete system outage. Resolution target: 2 hours.",
  },
  {
    value: "P2",
    label: "P2 - High",
    color: "#fd7e14",
    bg: "#fff5ec",
    description: "Major feature degradation. Resolution target: 4 hours.",
  },
  {
    value: "P3",
    label: "P3 - Medium",
    color: "#ffc107",
    bg: "#fffbec",
    description:
      "Partial impairment with workaround. Resolution target: 24 hours.",
  },
  {
    value: "P4",
    label: "P4 - Low",
    color: "#0d6efd",
    bg: "#eff5ff",
    description: "Minor or cosmetic issue. Resolution target: 48 hours.",
  },
];

const WIZARD_STEPS = [
  { key: "issue", label: "Issue Details", icon: "1" },
  { key: "describe", label: "Description & Files", icon: "2" },
  { key: "contact", label: "Review & Submit", icon: "3" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000),
    hrs = Math.floor(mins / 60),
    days = Math.floor(hrs / 24);
  if (days > 0) return `${days}d ago`;
  if (hrs > 0) return `${hrs}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return "just now";
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Convert plain-text URLs into clickable hyperlinks
function renderMessageWithLinks(text) {
  if (!text) return null;

  const urlRegex = /https?:\/\/[^\s<>"']+/gi;
  const content = String(text);
  const result = [];

  let lastIndex = 0;

  for (const match of content.matchAll(urlRegex)) {
    const start = match.index;
    const originalUrl = match[0];

    if (start > lastIndex) {
      result.push(content.slice(lastIndex, start));
    }

    // Remove punctuation from the end of URLs
    const url = originalUrl.replace(/[.,;!?)}\]]+$/, "");
    const trailing = originalUrl.slice(url.length);

    try {
      const parsed = new URL(url);

      if (
        parsed.protocol !== "https:" &&
        parsed.protocol !== "http:"
      ) {
        result.push(originalUrl);
      } else {
        result.push(
          <a
            key={start}
            href={parsed.href}
            target="_blank"
            rel="noopener noreferrer"
            className="sp-message-link"
          >
            {url}
          </a>
        );

        if (trailing) result.push(trailing);
      }
    } catch {
      result.push(originalUrl);
    }

    lastIndex = start + originalUrl.length;
  }

  result.push(content.slice(lastIndex));

  return result;
}

function getStatusStyle(s) {
  return (
    {
      open: { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
      pending: { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
      resolved: { bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
      closed: { bg: "#f3f4f6", color: "#6b7280", dot: "#9ca3af" },
    }[(s || "").toLowerCase()] || {
      bg: "#e0f2fe",
      color: "#0369a1",
      dot: "#0ea5e9",
    }
  );
}

function getPriorityObj(bp) {
  const pVal =
    { Urgent: "P1", High: "P2", Medium: "P3", Low: "P4" }[bp] || "P3";
  return PRIORITIES.find((p) => p.value === pVal) || PRIORITIES[2];
}

function extractUserInfo(user) {
  if (!user) {
    return {
      name: "",
      email: "",
      phone: "",
      phoneVerified: false,
      userId: "",
      initials: "",
    };
  }

  const name =
    user.name ||
    user["custom:name"] ||
    user["cognito:username"] ||
    "";

  const email = user.email || "";

  const phone = String(
    user.phone_number ||
    user.phone ||
    user["custom:phone_number"] ||
    ""
  ).replace(/\D/g, "");

  const phoneVerified =
    user.phone_number_verified === true ||
    user.phone_number_verified === "true";

  const userId = user.sub || user["cognito:username"] || "";

  const initials = name
    ? name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : email
      ? email[0].toUpperCase()
      : "U";

  return {
    name,
    email,
    phone,
    phoneVerified,
    userId,
    initials,
  };
}

function Support({ user }) {
  const userInfo = extractUserInfo(user);

  const [view, setView] = useState("list");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [activeTicketTab, setActiveTicketTab] = useState("conversation");

  // Ticket list
  const [allTickets, setAllTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(true);
  const [ticketListError, setTicketListError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Form (wizard)
  const [wizardStep, setWizardStep] = useState(0);
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [requestType, setRequestType] = useState("");
  const [platform, setPlatform] = useState("");
  const [priority, setPriority] = useState("P3");
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [fileUploadMsg, setFileUploadMsg] = useState("");
  const [name, setName] = useState(userInfo.name);
  const [phone, setPhone] = useState(userInfo.phone);
  const [email, setEmail] = useState(userInfo.email);
  const [phoneError, setPhoneError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [stepErrors, setStepErrors] = useState({});

  // Chat
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [chatFile, setChatFile] = useState(null);
  const [chatFileError, setChatFileError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const messagesEndRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [isChatFileDragging, setIsChatFileDragging] = useState(false);

  // ── Fetch tickets ──
  // Was GET /filter/requester?email=<anything>, which returned any customer's
  // full ticket history to anyone who typed their address. The email is now the
  // caller's own, taken from the token server-side, and cannot be supplied here.
  const fetchAllTickets = async () => {
    setIsLoadingTickets(true);
    setTicketListError("");
    try {
      const base = resolveBaseUrl(GET_TICKETS_BY_REQUESTER_API);
      const res = await api.get(`${base}/api/helpdesk/tickets/customer`);
      if (res.data?.success) setAllTickets(res.data.data || []);
    } catch (err) {
      console.error(err);
      setTicketListError("Unable to load your tickets. Please try again.");
    } finally {
      setIsLoadingTickets(false);
    }
  };

  useEffect(() => {
    if (userInfo.email || userInfo.userId) fetchAllTickets();
  }, [userInfo.email, userInfo.userId]);
  useEffect(() => {
    const info = extractUserInfo(user);
    if (info.name && !name) setName(info.name);
    if (info.email && !email) setEmail(info.email);
    if (info.phone && !phone) setPhone(info.phone);
  }, [user]);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  useEffect(() => {
    if (view !== "chat" || !ticket) return;
    const id = setInterval(() => refreshMessages(true), 30000);
    return () => clearInterval(id);
  }, [view, ticket]);

  useEffect(() => {
    document.body.classList.add("support-page-scroll-lock");

    return () => {
      document.body.classList.remove("support-page-scroll-lock");
    };
  }, []);

  const mapPriority = (p) =>
    ({
      P1: "Urgent",
      P2: "High",
      P3: "Medium",
      P4: "Low",
    })[p] || "Medium";
  const handlePhoneChange = (val) => {
    setPhone(val);
    setPhoneError(
      val.replace(/\D/g, "").length > 12
        ? "Phone number cannot exceed 10 digits"
        : "",
    );
  };

  const processTicketFiles = (selectedFiles) => {
    const incomingFiles = Array.from(selectedFiles || []);
    const validFiles = [];
    const errors = [];

    incomingFiles.forEach((selectedFile) => {
      const error = validateAttachment(selectedFile);

      if (error) {
        errors.push(`${selectedFile.name}: ${error}`);
      } else {
        validFiles.push(selectedFile);
      }
    });

    setFiles((currentFiles) => {
      const uniqueFiles = validFiles.filter(
        (newFile) =>
          !currentFiles.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size &&
              existingFile.lastModified === newFile.lastModified,
          ),
      );

      const remainingSlots = MAX_TICKET_ATTACHMENTS - currentFiles.length;

      const updatedFiles = [
        ...currentFiles,
        ...uniqueFiles.slice(0, Math.max(remainingSlots, 0)),
      ];

      if (uniqueFiles.length > remainingSlots) {
        errors.push(
          `Maximum ${MAX_TICKET_ATTACHMENTS} attachments are allowed.`,
        );
      }

      setFileUploadMsg(
        updatedFiles.length
          ? `${updatedFiles.length} attachment${
              updatedFiles.length > 1 ? "s" : ""
            } selected.`
          : "",
      );

      return updatedFiles;
    });

    setFileError(errors.join(" "));
  };

  const removeTicketFile = (indexToRemove) => {
    setFiles((currentFiles) =>
      currentFiles.filter((_, index) => index !== indexToRemove),
    );

    setFileError("");
  };

  const processChatFile = (selectedFile) => {
    const err = validateAttachment(selectedFile);

    if (err) {
      setChatFileError(err);
      setChatFile(null);
      return;
    }

    setChatFileError("");
    setChatFile(selectedFile);
  };

  const handleFileChange = (e) => {
    processTicketFiles(e.target.files);
    e.target.value = "";
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoading) {
      setIsFileDragging(true);
    }
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsFileDragging(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsFileDragging(false);

    if (isLoading) return;

    const droppedFiles = Array.from(e.dataTransfer.files || []);

    if (droppedFiles.length === 0) return;

    processTicketFiles(droppedFiles);
  };

  const handleChatFileChange = (e) => {
    processChatFile(e.target.files?.[0] || null);
    e.target.value = "";
  };

  const handleChatDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isSending) {
      setIsChatFileDragging(true);
    }
  };

  const handleChatDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsChatFileDragging(false);
    }
  };

  const handleChatFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsChatFileDragging(false);

    if (isSending) return;

    const droppedFiles = Array.from(e.dataTransfer.files || []);

    if (droppedFiles.length === 0) return;

    if (droppedFiles.length > 1) {
      setChatFileError("Only one attachment can be added.");
      setChatFile(null);
      return;
    }

    processChatFile(droppedFiles[0]);
  };

  const clearChatFile = () => {
    setChatFile(null);
    setChatFileError("");
  };

  // X-Requester-Email is gone. Ownership is proven server-side against the
  // token, so the header was only ever a suggestion the caller could change.
 const handleDownloadAttachment = async (
  messageId,
  attachmentId
) => {
  if (!ticket || downloadingId) return;

  const downloadKey = `${messageId}-${attachmentId}`;

  setDownloadingId(downloadKey);
  setReplyError("");

  try {
    const base = resolveBaseUrl(ATTACHMENT_DOWNLOAD_API);

    const res = await api.get(
      `${base}/api/helpdesk/tickets/customer/${ticket.ticketId}/attachment/${messageId}/${attachmentId}`
    );

    if (res.data?.success && res.data.data?.downloadUrl) {
      window.open(
        res.data.data.downloadUrl,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      setReplyError(
        res.data?.message || "Could not open the attachment."
      );
    }
  } catch (err) {
    console.error(err);

    setReplyError(
      err.response?.data?.message ||
        "Could not open the attachment. Please try again."
    );
  } finally {
    setDownloadingId(null);
  }
};

  const openTicketChat = async (t) => {
    setSelectedTicketId(t.ticketId);
    setTicket(t);
    setMessages(t.messages || []);
    setActiveTicketTab("conversation");
    setView("chat");
    try {
      const base = resolveBaseUrl(GET_TICKET_BY_ID_API);
      const res = await api.get(
        `${base}/api/helpdesk/tickets/customer/${t.ticketId}`,
      );
      if (res.data?.success) {
        setTicket(res.data.data);
        setMessages(res.data.data.messages || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // requesterName, requesterEmail and requesterUserId are no longer sent. The
  // server takes all three from the token. Previously the body chose who the
  // confirmation email went to, which made this an open relay on the SES domain.
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isLoading) return; // guard against double submission
    setIsLoading(true);
    setSubmitError("");
    try {
      const base = resolveBaseUrl(CREATE_TICKET_API);
      let attachments = [];

      if (files.length > 0) {
        try {
          attachments = await Promise.all(
            files.map((selectedFile) => uploadAttachment(selectedFile, null)),
          );
        } catch (uploadErr) {
          console.error(uploadErr);
          setSubmitError(
            "One or more files could not be uploaded, so the ticket was not submitted. Remove the attachment or try again.",
          );
          setIsLoading(false);
          return;
        }
      }
    const res = await api.post(
  `${base}/api/helpdesk/tickets/customer`,
  {
    subject: summary,
    description,
    type: requestType,
    priority: mapPriority(priority),
    tags: [platform, priority].filter(Boolean),
    phone,
    attachments,
  },
  {
    headers: {
      "Content-Type": "application/json",
    },
  }
);
      if (res.data?.success) {
        const t = res.data.data;
        setTicket(t);
        setMessages(t.messages || []);
        setAllTickets((prev) => [t, ...prev]);
        setSelectedTicketId(t.ticketId);
        setView("chat");
      }
    } catch (err) {
      console.error(err);
      setSubmitError(
        "Something went wrong. Please try again or contact us directly.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const refreshMessages = async (silent = false) => {
    if (!ticket) return;
    if (!silent) setIsRefreshing(true);
    try {
      const base = resolveBaseUrl(GET_TICKET_BY_ID_API);
      const res = await api.get(
        `${base}/api/helpdesk/tickets/customer/${ticket.ticketId}`,
      );
      if (res.data?.success) {
        setTicket(res.data.data);
        setMessages(res.data.data.messages || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setIsRefreshing(false);
    }
  };

  // type, author and authorInitials are no longer sent. The server sets all
  // three from the token. Sending type:"agent" previously reached the agent
  // email path and delivered caller-authored text to the real customer from
  // the ServQual domain on a genuine ticket thread.
  const handleSendReply = async () => {
    if (isSending || !ticket) return;
    if (!replyText.trim() && !chatFile) return;
    setIsSending(true);
    setReplyError("");
    try {
      const base = resolveBaseUrl(ADD_REPLY_API);

      let attachment = null;
      if (chatFile) {
        try {
          attachment = await uploadAttachment(chatFile, ticket.ticketId);
        } catch (uploadErr) {
          console.error(uploadErr);
          setReplyError(
            "Your file could not be uploaded, so the message was not sent. Try again or remove the attachment.",
          );
          setIsSending(false);
          return;
        }
      }

      // body is @NotBlank server-side, so an attachment-only message carries a
      // short generated body rather than changing the contract.
      const body = replyText.trim() || `Attached: ${chatFile.name}`;

      const res = await api.post(
        `${base}/api/helpdesk/tickets/customer/${ticket.ticketId}/reply`,
        { body, attachment },
        { headers: { "Content-Type": "application/json" } },
      );
      if (res.data?.success) {
        setTicket(res.data.data);
        setMessages(res.data.data.messages || []);
        setReplyText("");
        clearChatFile();
      } else {
        setReplyError(res.data?.message || "Failed to send. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setReplyError(
        err.response?.data?.message || "Failed to send. Please try again.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const goBackToList = () => {
    setView("list");
    setSelectedTicketId(null);
    fetchAllTickets();
  };

  const openNewTicketForm = () => {
    setTicket(null);
    setMessages([]);
    setSelectedTicketId(null);
    setWizardStep(0);
    setSummary("");
    setDescription("");
    setRequestType("");
    setPlatform("");
    setPriority("P3");
    setFiles([]);
    setPhone(userInfo.phone || "");
    setFileError("");
    setFileUploadMsg("");
    setPhoneError("");
    setSubmitError("");
    setReplyError("");
    setStepErrors({});
    setChatFile(null);
    setChatFileError("");
    setDownloadingId(null);
    setView("form");
  };

  // ── Wizard validation ──
  const validateStep = (step) => {
    const errs = {};
    if (step === 0) {
      if (!summary.trim()) errs.summary = "Subject is required";
      if (!requestType) errs.requestType = "Request type is required";
      if (!platform) errs.platform = "Platform is required";
    }
    if (step === 1) {
      if (!description.trim()) errs.description = "Description is required";
    }
    if (step === 2) {
      if (!name.trim()) errs.name = "Name is required";
      if (!email.trim()) errs.email = "Email is required";
      if (!phone || phone.replace(/\D/g, "").length < 6)
        errs.phone = "Valid phone number is required";
    }
    setStepErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    if (validateStep(wizardStep)) setWizardStep((s) => Math.min(s + 1, 2));
  };
  const prevStep = () => {
    setStepErrors({});
    setWizardStep((s) => Math.max(s - 1, 0));
  };

  // ── Filtered tickets ──
  const filteredTickets = allTickets.filter((t) => {
    const matchStatus =
      statusFilter === "all" || (t.status || "").toLowerCase() === statusFilter;
    const matchSearch =
      !searchQuery ||
      (t.subject || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.ticketId || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });
  const statusCounts = {
    all: allTickets.length,
    open: allTickets.filter((t) => (t.status || "").toLowerCase() === "open")
      .length,
    pending: allTickets.filter(
      (t) => (t.status || "").toLowerCase() === "pending",
    ).length,
    resolved: allTickets.filter(
      (t) => (t.status || "").toLowerCase() === "resolved",
    ).length,
    closed: allTickets.filter(
      (t) => (t.status || "").toLowerCase() === "closed",
    ).length,
  };
  // Notes no longer reach the client at all; the server strips them. Kept as
  // belt and braces in case an older response shape is cached.
  const getLastMessage = (t) => {
    const msgs = (t.messages || []).filter((m) => m.type !== "note");
    return msgs.length ? msgs[msgs.length - 1] : null;
  };

  const selectedPri =
    PRIORITIES.find((p) => p.value === priority) || PRIORITIES[2];

  const getTicketPlatform = () => {
    const tags = Array.isArray(ticket?.tags) ? ticket.tags.filter(Boolean) : [];

    const platformTag = tags.find(
      (tag) => !/^P[1-4]$/i.test(String(tag || "").trim()),
    );

    return platformTag || ticket?.platform || "—";
  };

const getTicketAttachments = () => {
  const allAttachments = [];

  (messages || [])
    .filter((msg) => msg?.type !== "note")
    .forEach((msg) => {

      // New multiple-attachment format
      if (Array.isArray(msg.attachments)) {
        msg.attachments.forEach((attachment, index) => {
          allAttachments.push({
            id:
              attachment.attachmentId ||
              attachment.s3Key ||
              `${msg.messageId}-${index}`,
            messageId: msg.messageId,
            attachmentId: attachment.attachmentId,
            fileName:
              attachment.fileName || "Attachment",
            sizeBytes:
              attachment.sizeBytes || 0,
            uploadedAt:
              attachment.uploadedAt || msg.timestamp,
            uploadedBy:
              attachment.uploadedBy ||
              (msg.type === "customer"
                ? "Customer"
                : "Support"),
          });
        });
      }

      // Legacy single-attachment format
      else if (msg.attachment) {
        allAttachments.push({
          id:
            msg.attachment.attachmentId ||
            msg.attachment.s3Key ||
            msg.messageId,
          messageId: msg.messageId,
          attachmentId:
            msg.attachment.attachmentId,
          fileName:
            msg.attachment.fileName || "Attachment",
          sizeBytes:
            msg.attachment.sizeBytes || 0,
          uploadedAt:
            msg.attachment.uploadedAt || msg.timestamp,
          uploadedBy:
            msg.attachment.uploadedBy ||
            (msg.type === "customer"
              ? "Customer"
              : "Support"),
        });
      }
    });

  return allAttachments;
};
  const getTicketDetailRows = () => {
    if (!ticket) return [];

    const priorityObj = getPriorityObj(ticket.priority);

    return [
      { label: "Ticket ID", value: ticket.ticketId || "—" },
      { label: "Status", value: ticket.status || "—" },
      { label: "Priority", value: priorityObj.label || ticket.priority || "—" },
      { label: "Request Type", value: ticket.type || "—" },
      { label: "Platform", value: getTicketPlatform() },
      { label: "Created On", value: formatDate(ticket.createdAt) || "—" },
      {
        label: "Response Due",
        value: formatDate(ticket.firstResponseDue) || "—",
      },
      {
        label: "Resolution Due",
        value: formatDate(ticket.resolutionDue) || "—",
      },
      { label: "Assigned To", value: ticket.assignedTo || "Not assigned" },
      {
        label: "Requester",
        value: ticket.requesterEmail || userInfo.email || "—",
      },
    ];
  };

  const getTicketActivityItems = () => {
    if (!ticket) return [];

    const items = [
      {
        id: "created",
        type: "created",
        title: "Ticket created",
        description: `Ticket created by ${
          ticket.requesterName || userInfo.name || "customer"
        }`,
        timestamp: ticket.createdAt,
        icon: <CheckCircle size={14} />,
      },
    ];

    (messages || [])
  .filter((msg) => msg?.type !== "note")
  .forEach((msg, index) => {
    const isCustomer = msg.type === "customer";

    const messageAttachments = Array.isArray(msg.attachments)
      ? msg.attachments
      : msg.attachment
        ? [msg.attachment]
        : [];

    items.push({
      id: msg.messageId || `message-${index}`,
      type: isCustomer ? "customer" : "support",
      title: isCustomer ? "Customer replied" : "Support replied",
      description:
        messageAttachments.length > 0
          ? `Message added with ${messageAttachments.length} attachment${
              messageAttachments.length > 1 ? "s" : ""
            }`
          : String(msg.body || "").slice(0, 90),
      timestamp: msg.timestamp,
      icon: isCustomer
        ? <MessageSquare size={14} />
        : <User size={14} />,
    });
  });

    if (
      ["closed", "resolved"].includes(String(ticket.status || "").toLowerCase())
    ) {
      items.push({
        id: "closed",
        type: "closed",
        title: `Ticket ${ticket.status}`,
        description: "Ticket is no longer accepting replies.",
        timestamp: ticket.updatedAt || ticket.closedAt || ticket.resolvedAt,
        icon: <CheckCircle size={14} />,
      });
    }

    return items
      .filter((item) => item.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };
  // ═══════════════════════════════════════════════════════════════
  // SPLIT-PANEL: LIST + CHAT
  // ═══════════════════════════════════════════════════════════════
  if (view === "list" || view === "chat") {
    const priObj = ticket ? getPriorityObj(ticket.priority) : null;
    const statusSt = ticket ? getStatusStyle(ticket.status) : null;
    const isClosed =
      ticket &&
      ["closed", "resolved"].includes((ticket.status || "").toLowerCase());

    return (
      <>
        <style>{STYLES}</style>
        <div className="sp-layout">
          {/* LEFT PANEL */}
          <div className="sp-sidebar">
            <div className="sp-sidebar__header">
              <div className="sp-sidebar__title-row">
                <h3 className="sp-sidebar__title">My Tickets</h3>
                <button
                  className="sp-new-btn"
                  onClick={openNewTicketForm}
                  title="New Ticket"
                >
                  <PlusCircle size={16} />
                </button>
              </div>
              <div className="sp-search">
                <Search size={14} color="#9ca3af" />
                <input
                  type="text"
                  placeholder="Search tickets…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="sp-filters">
                {["all", "open", "pending", "resolved", "closed"].map((s) => (
                  <button
                    key={s}
                    className={`sp-filter-btn ${statusFilter === s ? "sp-filter-btn--active" : ""}`}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s === "all"
                      ? "All"
                      : s.charAt(0).toUpperCase() + s.slice(1)}
                    {statusCounts[s] > 0 && (
                      <span className="sp-filter-count">{statusCounts[s]}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="sp-sidebar__list">
              {isLoadingTickets ? (
                <div className="sp-loading">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span>Loading…</span>
                </div>
              ) : ticketListError ? (
                <div className="sp-list-error">
                  <AlertCircle size={16} />
                  <span>{ticketListError}</span>
                  <button onClick={fetchAllTickets} className="sp-retry">
                    Retry
                  </button>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="sp-empty">
                  <Inbox size={36} color="#d1d5db" strokeWidth={1.5} />
                  <p>
                    {allTickets.length === 0
                      ? "No tickets yet"
                      : "No matching tickets"}
                  </p>
                  {allTickets.length === 0 && (
                    <button
                      className="sp-empty-cta"
                      onClick={openNewTicketForm}
                    >
                      Create your first ticket
                    </button>
                  )}
                </div>
              ) : (
                filteredTickets.map((t) => {
                  const tPri = getPriorityObj(t.priority);
                  const tSt = getStatusStyle(t.status);
                  const lastMsg = getLastMessage(t);
                  const msgCount = (t.messages || []).filter(
                    (m) => m.type !== "note",
                  ).length;
                  return (
                    <div
                      key={t.ticketId}
                      className={`sp-ticket-row ${selectedTicketId === t.ticketId ? "sp-ticket-row--active" : ""}`}
                      onClick={() => openTicketChat(t)}
                    >
                      <div className="sp-ticket-row__top">
                        <div className="sp-ticket-row__status">
                          <span
                            className="sp-status-dot"
                            style={{ background: tSt.dot }}
                          />
                          <code className="sp-ticket-id">{t.ticketId}</code>
                        </div>
                        <span className="sp-ticket-row__time">
                          {timeAgo(t.createdAt)}
                        </span>
                      </div>
                      <h6 className="sp-ticket-row__subject">{t.subject}</h6>
                      {lastMsg && (
                        <p className="sp-ticket-row__preview">
                          <span className="sp-preview-author">
                            {lastMsg.type === "customer"
                              ? "You"
                              : lastMsg.author || "Agent"}
                            :
                          </span>{" "}
                          {(lastMsg.body || "").slice(0, 80)}
                          {(lastMsg.body || "").length > 80 ? "…" : ""}
                        </p>
                      )}
                      <div className="sp-ticket-row__meta">
                        <span className="sp-ticket-row__type">
                          {t.type || "-"}
                        </span>
                        <div className="sp-ticket-row__badges">
                          <span
                            className="sp-mini-pill"
                            style={{ background: tSt.bg, color: tSt.color }}
                          >
                            {t.status}
                          </span>
                          <span
                            className="sp-mini-pill"
                            style={{ background: tPri.bg, color: tPri.color }}
                          >
                            {tPri.value}
                          </span>
                          {msgCount > 0 && (
                            <span className="sp-msg-count">
                              <MessageSquare size={10} /> {msgCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="sp-main">
            {!ticket ? (
              <div className="sp-welcome">
                <div className="sp-welcome__icon">
                  <Headphones size={40} color="#0d6efd" strokeWidth={1.5} />
                </div>
                <h3 className="sp-welcome__title">Support Center</h3>
                <p className="sp-welcome__desc">
                  Select a ticket from the left to view the conversation, or
                  create a new one.
                </p>
                <button className="sp-welcome__cta" onClick={openNewTicketForm}>
                  <PlusCircle size={15} /> New Support Request
                </button>
              </div>
            ) : (
              <>
                <div className="sp-chat-header">
                  <div className="sp-chat-header__left">
                    <button className="sp-back-mobile" onClick={goBackToList}>
                      <ArrowLeft size={16} />
                    </button>
                    <div>
                      <div className="sp-chat-header__badges">
                        <code className="sp-ticket-id">{ticket.ticketId}</code>
                        <span
                          className="sp-mini-pill"
                          style={{
                            background: statusSt.bg,
                            color: statusSt.color,
                          }}
                        >
                          {ticket.status}
                        </span>
                        {/* <span className="sp-mini-pill" style={{ background: priObj.bg, color: priObj.color }}>{priObj.value} {priObj.label.split('-')[1]?.trim()}</span> */}
                      </div>
                      <h4 className="sp-chat-header__subject">
                        {ticket.subject}
                      </h4>
                      <div className="sp-chat-header__meta">
                        <Clock size={11} /> {timeAgo(ticket.createdAt)}
                        {ticket.assignedTo && (
                          <>
                            <span className="sp-meta-sep">·</span>
                            <User size={11} /> {ticket.assignedTo}
                          </>
                        )}
                        {ticket.type && (
                          <>
                            <span className="sp-meta-sep">·</span>
                            {ticket.type}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="sp-chat-header__actions">
                    <button
                      className="hdr-btn"
                      onClick={() => refreshMessages(false)}
                      disabled={isRefreshing}
                    >
                      <RefreshCw
                        size={13}
                        className={isRefreshing ? "spin" : ""}
                      />
                    </button>
                    <button
                      className="hdr-btn hdr-btn--blue"
                      onClick={openNewTicketForm}
                    >
                      <PlusCircle size={13} /> New
                    </button>
                  </div>
                </div>
                {/* <div className="sp-info-bar">
                  {[{ label:'Priority', value:priObj.label, style:{ color:priObj.color, fontWeight:600 } },
                    { label:'Opened', value:formatDate(ticket.createdAt) },
                    { label:'Response Due', value:formatDate(ticket.firstResponseDue) },
                    { label:'Resolution Due', value:formatDate(ticket.resolutionDue) },
                  ].map(item => (
                    <div key={item.label} className="sp-info-bar__item">
                      <span className="sp-info-bar__label">{item.label}</span>
                      <span className="sp-info-bar__value" style={item.style}>{item.value}</span>
                    </div>
                  ))}
                </div>
                {ticket.tags?.filter(Boolean).length > 0 && (
                  <div className="sp-tags"><Tag size={11} color="#9ca3af" />{ticket.tags.filter(Boolean).map(t => <span key={t} className="tag-pill">{t}</span>)}</div>
                )} */}
                <div className="sp-ticket-tabs">
                  <button
                    type="button"
                    className={`sp-ticket-tab ${
                      activeTicketTab === "conversation"
                        ? "sp-ticket-tab--active"
                        : ""
                    }`}
                    onClick={() => setActiveTicketTab("conversation")}
                  >
                    <MessageSquare size={14} />
                    Conversation
                  </button>

                  <button
                    type="button"
                    className={`sp-ticket-tab ${
                      activeTicketTab === "details"
                        ? "sp-ticket-tab--active"
                        : ""
                    }`}
                    onClick={() => setActiveTicketTab("details")}
                  >
                    <FileText size={14} />
                    Details
                  </button>
                </div>
                {activeTicketTab === "conversation" ? (
                  <>
                    <div className="sp-messages">
                      <div className="sys-msg">
                        <CheckCircle size={14} /> Ticket{" "}
                        <strong>{ticket.ticketId}</strong> created. Confirmation
                        sent to <strong>{ticket.requesterEmail}</strong>.
                      </div>
                      {messages
                        .filter((m) => m.type !== "note")
                        .map((msg, i) => {
                          const isCust = msg.type === "customer";
                          return (
                            <div
                              key={msg.messageId || i}
                              className={`bubble-row ${isCust ? "bubble-row--right" : "bubble-row--left"}`}
                            >
                              <div
                                className={`b-avatar ${isCust ? "b-avatar--you" : "b-avatar--agent"}`}
                              >
                                {isCust
                                  ? userInfo.initials || "U"
                                  : msg.authorInitials || "SQ"}
                              </div>
                              <div className="b-content">
                                <div
                                  className={`b-meta ${isCust ? "b-meta--right" : ""}`}
                                >
                                  <span className="b-name">
                                    {isCust
                                      ? "You"
                                      : msg.author || "Support Team"}
                                  </span>
                                  {!isCust && (
                                    <span className="agent-pill">Agent</span>
                                  )}
                                  <span className="b-time">
                                    {timeAgo(msg.timestamp)}
                                  </span>
                                </div>
                               <div
  className={`bubble ${isCust ? "bubble--you" : "bubble--agent"}`}
>
  {renderMessageWithLinks(msg.body)}
</div>
                              {(
  Array.isArray(msg.attachments)
    ? msg.attachments
    : msg.attachment
      ? [msg.attachment]
      : []
).map((attachment, attachmentIndex) => (
  <button
    key={
      attachment.attachmentId ||
      attachment.s3Key ||
      `${msg.messageId}-${attachmentIndex}`
    }
    type="button"
    className="attach-chip"
    onClick={() =>
      handleDownloadAttachment(
        msg.messageId,
        attachment.attachmentId
      )
    }
    disabled={
      downloadingId ===
      `${msg.messageId}-${attachment.attachmentId}`
    }
    title={attachment.fileName}
  >
    {downloadingId ===
    `${msg.messageId}-${attachment.attachmentId}` ? (
      <Spinner animation="border" size="sm" />
    ) : (
      <Paperclip size={12} />
    )}

    <span className="attach-chip__name">
      {attachment.fileName}
    </span>

    <span className="attach-chip__size">
      {formatBytes(attachment.sizeBytes)}
    </span>
  </button>
))}
                                <div
                                  className={`b-ts ${isCust ? "b-ts--right" : ""}`}
                                >
                                  {formatDate(msg.timestamp)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      <div ref={messagesEndRef} />
                    </div>
                    {isClosed ? (
                      <div className="closed-bar">
                        <CheckCircle size={15} /> This ticket is{" "}
                        <strong style={{ marginLeft: 3 }}>
                          {ticket.status}
                        </strong>
                        .
                        <button
                          className="btn btn-link btn-sm p-0 ms-2 fw-semibold"
                          style={{ color: "#166534" }}
                          onClick={openNewTicketForm}
                        >
                          Open a new ticket →
                        </button>
                      </div>
                    ) : (
                      <div className="sp-reply">
                        {replyError && (
                          <div className="sp-reply-error">
                            <AlertCircle size={13} /> {replyError}
                          </div>
                        )}
                        <div
                          className={`sp-reply__wrap ${
                            isChatFileDragging ? "sp-reply__wrap--dragging" : ""
                          }`}
                          onDragEnter={handleChatDragOver}
                          onDragOver={handleChatDragOver}
                          onDragLeave={handleChatDragLeave}
                          onDrop={handleChatFileDrop}
                        >
                          {isChatFileDragging && (
                            <div className="sp-reply__drop-overlay">
                              <Upload size={20} />
                              <span>Drop attachment here</span>
                            </div>
                          )}
                          <textarea
                            rows={3}
                            placeholder="Type your message… (Ctrl+Enter to send)"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                                handleSendReply();
                            }}
                          />
                          {chatFile && (
                            <div className="sp-reply__file">
                              <FileText size={13} />
                              <span className="sp-reply__file-name">
                                {chatFile.name}
                              </span>
                              <span className="sp-reply__file-size">
                                {formatBytes(chatFile.size)}
                              </span>
                              <button
                                type="button"
                                className="sp-reply__file-x"
                                onClick={clearChatFile}
                                disabled={isSending}
                                title="Remove attachment"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          )}
                          {chatFileError && (
                            <div className="sp-reply-error">
                              <AlertCircle size={13} /> {chatFileError}
                            </div>
                          )}
                          <div className="sp-reply__footer">
                            <div className="sp-reply__footer-left">
                              <button
                                type="button"
                                className="attach-btn"
                                onClick={() =>
                                  chatFileInputRef.current?.click()
                                }
                                disabled={isSending}
                                title="Attach a file"
                              >
                                <Paperclip size={14} />
                              </button>
                              <input
                                type="file"
                                ref={chatFileInputRef}
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={handleChatFileChange}
                                style={{ display: "none" }}
                              />
                              <span className="sp-reply__hint">
                                Ctrl + Enter to send · PDF, PNG, JPG up to 10 MB
                              </span>
                            </div>
                            <button
                              className="send-btn"
                              onClick={handleSendReply}
                              disabled={
                                (!replyText.trim() && !chatFile) || isSending
                              }
                            >
                              {isSending ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <Send size={13} /> Send
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="sp-details-tab">
                    <div className="sp-details-grid">
                      <section className="sp-detail-card">
                        <div className="sp-detail-card__header">
                          <div>
                            <h5>Ticket Details</h5>
                            <p>Core request, ownership and SLA information.</p>
                          </div>
                          <span className="sp-detail-count">Info</span>
                        </div>

                        <div className="sp-detail-list">
                          {getTicketDetailRows().map((row) => (
                            <div key={row.label} className="sp-detail-row">
                              <span>{row.label}</span>
                              <strong title={row.value}>{row.value}</strong>
                            </div>
                          ))}
                        </div>
                      </section>

                      <section className="sp-detail-card">
                        <div className="sp-detail-card__header">
                          <div>
                            <h5>Attachments</h5>
                            <p>Files uploaded with the ticket and replies.</p>
                          </div>
                          <span className="sp-detail-count">
                            {getTicketAttachments().length} Files
                          </span>
                        </div>

                        <div className="sp-attachment-list">
                          {getTicketAttachments().length > 0 ? (
                            getTicketAttachments().map((att) => (
                              <div key={att.id} className="sp-attachment-row">
                                <div className="sp-attachment-icon">
                                  <Paperclip size={15} />
                                </div>

                                <div className="sp-attachment-meta">
                                  <strong title={att.fileName}>
                                    {att.fileName}
                                  </strong>
                                  <span>
                                    {formatBytes(att.sizeBytes)} ·{" "}
                                    {att.uploadedBy}
                                    {att.uploadedAt
                                      ? ` · ${formatDate(att.uploadedAt)}`
                                      : ""}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className="sp-attachment-download"
                                  disabled={
                                    !att.messageId ||
                                    downloadingId ===
  `${att.messageId}-${att.attachmentId}`
                                  }
                                 onClick={() =>
  handleDownloadAttachment(
    att.messageId,
    att.attachmentId
  )
}
                                >
                                  {downloadingId ===
  `${att.messageId}-${att.attachmentId}` ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    "Download"
                                  )}
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="sp-detail-empty">
                              <Paperclip size={22} />
                              <span>No attachments uploaded yet.</span>
                            </div>
                          )}
                        </div>
                      </section>

                      <section className="sp-detail-card sp-detail-card--wide">
                        <div className="sp-detail-card__header">
                          <div>
                            <h5>Activity Timeline</h5>
                            <p>Chronological view of ticket activity.</p>
                          </div>
                          <span className="sp-detail-count">
                            {getTicketActivityItems().length} Events
                          </span>
                        </div>

                        <div className="sp-activity-list">
                          {getTicketActivityItems().map((item) => (
                            <div key={item.id} className="sp-activity-item">
                              <div
                                className={`sp-activity-icon sp-activity-icon--${item.type}`}
                              >
                                {item.icon}
                              </div>

                              <div className="sp-activity-content">
                                <div className="sp-activity-top">
                                  <strong>{item.title}</strong>
                                  <span>{formatDate(item.timestamp)}</span>
                                </div>
                                <p>
                                  {item.description ||
                                    "No additional details available."}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </section>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <BackgroundFooter />
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // FORM VIEW: MULTI-STEP WIZARD
  // ═══════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════
  // FORM VIEW: MULTI-STEP WIZARD
  // ═══════════════════════════════════════════════════════════════
  return (
    <>
      <style>{STYLES}</style>

      <div className="sr-page">
        <div className="sr-topbar">
          <button className="wiz-back sr-back-btn" onClick={goBackToList}>
            <ArrowLeft size={15} />
            Back to My Tickets
          </button>

          <div>
            <h3 className="sr-page-title">New Support Request</h3>
            <p className="sr-page-subtitle">
              Submit a request with clear priority, issue context and supporting
              evidence.
            </p>
          </div>
        </div>

        <div className="sr-shell">
          <aside className="sr-side-panel">
            <div className="sr-side-kicker">ServQual Support</div>

            <h2 className="sr-side-title">
              Tell us what happened. We will route it to the right team.
            </h2>

            <p className="sr-side-text">
              Complete the 3-step request form with issue details, evidence and
              contact confirmation.
            </p>

            <div className="sr-help-list">
              <div className="sr-help-item">
                <span className="sr-help-icon">
                  <FileText size={17} />
                </span>
                <div>
                  <strong>Clear issue summary</strong>
                  <span>Helps triage and assign ownership faster.</span>
                </div>
              </div>

              <div className="sr-help-item">
                <span className="sr-help-icon">
                  <Paperclip size={17} />
                </span>
                <div>
                  <strong>Attach evidence</strong>
                  <span>Upload PDF, PNG or JPG up to 10 MB.</span>
                </div>
              </div>

              <div className="sr-help-item">
                <span className="sr-help-icon">
                  <Clock size={17} />
                </span>
                <div>
                  <strong>SLA driven priority</strong>
                  <span>P1 to P4 routing based on business impact.</span>
                </div>
              </div>
            </div>

            <div className="sr-sla-card">
              <div className="sr-sla-title">Selected priority</div>
              <div
                className="sr-sla-value"
                style={{ color: selectedPri.color }}
              >
                {selectedPri.label}
              </div>
              <div className="sr-sla-desc">{selectedPri.description}</div>
            </div>
          </aside>

          <main className="sr-main-panel">
            <div className="sr-progress-card">
              <div className="wiz-progress sr-progress">
                {WIZARD_STEPS.map((step, idx) => (
                  <div
                    key={step.key}
                    className={`wiz-step ${
                      idx < wizardStep ? "wiz-step--done" : ""
                    } ${idx === wizardStep ? "wiz-step--active" : ""}`}
                  >
                    <div className="wiz-step__circle">
                      {idx < wizardStep ? (
                        <CheckCircle size={16} />
                      ) : (
                        <span>{step.icon}</span>
                      )}
                    </div>

                    <span className="wiz-step__label">{step.label}</span>

                    {idx < WIZARD_STEPS.length - 1 && (
                      <div
                        className={`wiz-step__line ${
                          idx < wizardStep ? "wiz-step__line--done" : ""
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {submitError && (
              <div className="wiz-error sr-error">
                <AlertCircle size={16} />
                {submitError}
              </div>
            )}

            <div className="wiz-card sr-request-card">
              {wizardStep === 0 && (
                <div className="wiz-card__body">
                  <div className="sr-card-header">
                    <span className="sr-step-badge">Step 1 of 3</span>
                    <h5 className="wiz-card__heading">Issue Details</h5>
                    <p className="wiz-card__sub">
                      Share the basics so your request can be routed to the
                      correct support queue.
                    </p>
                  </div>

                  <div className="wiz-field">
                    <label className="wiz-label">
                      Subject Summary <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className={`wiz-input ${
                        stepErrors.summary ? "wiz-input--err" : ""
                      }`}
                      placeholder="Example: Unable to access SUSAN dashboard"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                    />
                    {stepErrors.summary && (
                      <span className="wiz-field-err">
                        {stepErrors.summary}
                      </span>
                    )}
                  </div>

                  <div className="wiz-row">
                    <div className="wiz-field wiz-field--half">
                      <label className="wiz-label">
                        Request Type <span className="req">*</span>
                      </label>
                      <select
                        className={`wiz-select ${
                          stepErrors.requestType ? "wiz-input--err" : ""
                        }`}
                        value={requestType}
                        onChange={(e) => setRequestType(e.target.value)}
                      >
                        <option value="">Select Request Type</option>
                        <option value="Unicorn Security Assessment Support">
                          SUSAN Support
                        </option>
                        <option value="Unicorn Security Assessment Feature">
                          SUSAN Feature
                        </option>
                        <option value="Unicorn Security Assessment Error">
                          SUSAN Error
                        </option>
                        <option value="Virus Attack">Virus Attack</option>
                        <option value="Antivirus Manufacturing">
                          Antivirus Manufacturing
                        </option>
                        <option value="Firewall Issue">Firewall Issue</option>
                        <option value="System Issue">System Issue</option>
                        <option value="Suspicious System Behaviour">
                          Suspicious System Behaviour
                        </option>
                        <option value="Website Attack">Website Attack</option>
                        <option value="Feedback">Feedback</option>
                      </select>
                      {stepErrors.requestType && (
                        <span className="wiz-field-err">
                          {stepErrors.requestType}
                        </span>
                      )}
                    </div>

                    <div className="wiz-field wiz-field--half">
                      <label className="wiz-label">
                        Platform <span className="req">*</span>
                      </label>
                      <select
                        className={`wiz-select ${
                          stepErrors.platform ? "wiz-input--err" : ""
                        }`}
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                      >
                        <option value="">Select Platform</option>
                        <option value="Windows">Windows</option>
                        <option value="Mac">Mac</option>
                        <option value="Linux">Linux</option>
                        <option value="Android">Android</option>
                        <option value="iOS">iOS</option>
                        <option value="Network / Firewall Appliance">
                          Network / Firewall Appliance
                        </option>
                        <option value="Cloud / Network">Cloud / Network</option>
                      </select>
                      {stepErrors.platform && (
                        <span className="wiz-field-err">
                          {stepErrors.platform}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="wiz-field">
                    <label className="wiz-label">
                      Priority Level <span className="req">*</span>
                    </label>

                    <div className="wiz-priority-row sr-priority-grid">
                      {PRIORITIES.map((p) => (
                        <button
                          type="button"
                          key={p.value}
                          className={`wiz-pri-chip sr-priority-card ${
                            priority === p.value ? "wiz-pri-chip--active" : ""
                          }`}
                          style={
                            priority === p.value
                              ? {
                                  borderColor: p.color,
                                  background: p.bg,
                                  color: p.color,
                                }
                              : {}
                          }
                          onClick={() => setPriority(p.value)}
                        >
                          <span
                            className="wiz-pri-dot"
                            style={{ background: p.color }}
                          />
                          <span className="sr-priority-code">{p.value}</span>
                          <span className="sr-priority-label">
                            {p.label.split("-")[1]?.trim()}
                          </span>
                        </button>
                      ))}
                    </div>

                    <p className="wiz-pri-desc sr-priority-desc">
                      <span
                        className="wiz-pri-dot"
                        style={{
                          background: selectedPri.color,
                          width: 6,
                          height: 6,
                        }}
                      />
                      <strong>{selectedPri.label}</strong>:{" "}
                      {selectedPri.description}
                    </p>
                  </div>
                </div>
              )}

              {wizardStep === 1 && (
                <div className="wiz-card__body">
                  <div className="sr-card-header">
                    <span className="sr-step-badge">Step 2 of 3</span>
                    <h5 className="wiz-card__heading">
                      Description & Evidence
                    </h5>
                    <p className="wiz-card__sub">
                      Add details, reproduction steps, error messages and
                      screenshots.
                    </p>
                  </div>

                  <div className="wiz-field">
                    <label className="wiz-label">
                      Description <span className="req">*</span>
                    </label>
                    <textarea
                      className={`wiz-textarea ${
                        stepErrors.description ? "wiz-input--err" : ""
                      }`}
                      rows={7}
                      placeholder="Describe the issue, impact, expected behavior and steps already tried."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                    {stepErrors.description && (
                      <span className="wiz-field-err">
                        {stepErrors.description}
                      </span>
                    )}
                  </div>

                  <div className="wiz-field">
                    <label className="wiz-label">
                      Attachment{" "}
                      <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                        optional
                      </span>
                    </label>

                    <div
                      className={`wiz-file-zone sr-file-zone ${
                        isFileDragging ? "wiz-file-zone--dragging" : ""
                      } ${files.length > 0 ? "wiz-file-zone--selected" : ""}`}
                      onDragEnter={handleFileDragOver}
                      onDragOver={handleFileDragOver}
                      onDragLeave={handleFileDragLeave}
                      onDrop={handleFileDrop}
                      onClick={() => {
                        if (!isLoading) {
                          document.getElementById("wiz-file")?.click();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Upload attachment"
                      onKeyDown={(e) => {
                        if (
                          (e.key === "Enter" || e.key === " ") &&
                          !isLoading
                        ) {
                          e.preventDefault();
                          document.getElementById("wiz-file")?.click();
                        }
                      }}
                    >
                      <div className="sr-upload-icon">
                        <Upload
                          size={22}
                          color={
                            isFileDragging || files.length > 0
                              ? "#024A82"
                              : "#64748b"
                          }
                        />
                      </div>

                      <div className="wiz-file-zone__content">
                        <span className="wiz-file-text">
                          {isFileDragging
                            ? "Drop attachments here"
                            : files.length > 0
                              ? "Add more attachments"
                              : "Drag and drop evidence or click to browse"}
                        </span>

                        <span className="wiz-file-hint">
                          PDF, PNG, JPG. Maximum 10 MB per file.
                        </span>
                      </div>
                      {files.length > 0 && (
                        <button
                          type="button"
                          className="wiz-file-remove"
                          disabled={isLoading}
                          title="Remove attachment"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFiles([]);
                            setFileError("");
                            setFileUploadMsg("");
                          }}
                        >
                          <X size={15} />
                        </button>
                      )}

                      <input
                        type="file"
                        id="wiz-file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        multiple
                        onChange={handleFileChange}
                        disabled={isLoading}
                        style={{ display: "none" }}
                      />
                    </div>

                    {/* Display all selected attachments here */}
                    {files.length > 0 && (
                      <div className="wiz-file-list">
                        {files.map((selectedFile, index) => (
                          <div
                            className="wiz-file-item"
                            key={`${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`}
                          >
                            <FileText size={16} />

                            <div className="wiz-file-item__meta">
                              <strong title={selectedFile.name}>
                                {selectedFile.name}
                              </strong>

                              <span>{formatBytes(selectedFile.size)}</span>
                            </div>

                            <button
                              type="button"
                              className="wiz-file-remove"
                              disabled={isLoading}
                              title="Remove attachment"
                              onClick={() => removeTicketFile(index)}
                            >
                              <X size={15} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {fileError && (
                      <span className="wiz-field-err">{fileError}</span>
                    )}
                    {fileUploadMsg && (
                      <span className="wiz-field-ok">
                        <CheckCircle size={12} />
                        {fileUploadMsg}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div className="wiz-card__body">
                  <div className="sr-card-header">
                    <span className="sr-step-badge">Step 3 of 3</span>
                    <h5 className="wiz-card__heading">Review & Submit</h5>
                    <p className="wiz-card__sub">
                      Confirm the request details before submitting to ServQual
                      Support.
                    </p>
                  </div>

                  <div className="wiz-review sr-review-box">
                    <div className="wiz-review__row">
                      <span className="wiz-review__label">Subject</span>
                      <span className="wiz-review__value">{summary}</span>
                    </div>

                    <div className="wiz-review__row">
                      <span className="wiz-review__label">Type</span>
                      <span className="wiz-review__value">{requestType}</span>
                    </div>

                    <div className="wiz-review__row">
                      <span className="wiz-review__label">Platform</span>
                      <span className="wiz-review__value">{platform}</span>
                    </div>

                    <div className="wiz-review__row">
                      <span className="wiz-review__label">Priority</span>
                      <span
                        className="wiz-review__value"
                        style={{ color: selectedPri.color, fontWeight: 700 }}
                      >
                        <span
                          className="wiz-pri-dot"
                          style={{ background: selectedPri.color }}
                        />
                        {selectedPri.label}
                      </span>
                    </div>

                    <div className="wiz-review__row">
                      <span className="wiz-review__label">Description</span>
                      <span className="wiz-review__value wiz-review__desc">
                        {description.slice(0, 140)}
                        {description.length > 140 ? "…" : ""}
                      </span>
                    </div>

                    {files.length > 0 && (
                      <div className="wiz-review__row">
                        <span className="wiz-review__label">Files</span>
                        <span className="wiz-review__value">
                          <FileText size={13} />
                          {files.length} attachment{files.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  {userInfo.email && (
                    <div className="wiz-autofill sr-autofill">
                      <CheckCircle size={14} />
                      Logged in as{" "}
                      <strong>{userInfo.name || userInfo.email}</strong>.
                      Contact details auto-filled.
                    </div>
                  )}

                  <div className="wiz-row">
                    <div className="wiz-field wiz-field--half">
                      <label className="wiz-label">
                        Full Name <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`wiz-input ${
                          stepErrors.name ? "wiz-input--err" : ""
                        }`}
                        placeholder="Your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        readOnly={!!userInfo.name}
                        style={
                          userInfo.name
                            ? {
                                backgroundColor: "#f8fafc",
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      />
                      {stepErrors.name && (
                        <span className="wiz-field-err">{stepErrors.name}</span>
                      )}
                    </div>

                    <div className="wiz-field wiz-field--half">
                      <label className="wiz-label">
                        Email Address <span className="req">*</span>
                      </label>
                      <input
                        type="email"
                        className={`wiz-input ${
                          stepErrors.email ? "wiz-input--err" : ""
                        }`}
                        placeholder="Official email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        readOnly={!!userInfo.email}
                        style={
                          userInfo.email
                            ? {
                                backgroundColor: "#f8fafc",
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      />
                      {stepErrors.email && (
                        <span className="wiz-field-err">
                          {stepErrors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* <div className="wiz-field">
                    <label className="wiz-label">
                      Phone Number <span className="req">*</span>
                    </label>
                  <PhoneInput
  country={(getUserRegion() || "GB").toLowerCase()}
  value={phone}
  onChange={handlePhoneChange}
  disabled={userInfo.phoneVerified}
  disableDropdown={userInfo.phoneVerified}
  inputProps={{
    name: "phone",
    required: true,
    readOnly: userInfo.phoneVerified,
    className: "wiz-input wiz-phone-input",
  }}
  containerClass="wiz-phone-container"
  buttonStyle={{
    backgroundColor: userInfo.phoneVerified ? "#f8fafc" : "white",
    border: "1px solid #dbe3ef",
    borderRight: "none",
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
  }}
  inputStyle={{
    width: "100%",
    height: "44px",
    border: "1px solid #dbe3ef",
    fontSize: "14px",
    backgroundColor: userInfo.phoneVerified
      ? "#f8fafc"
      : "#ffffff",
    cursor: userInfo.phoneVerified
      ? "not-allowed"
      : "text",
  }}
  dropdownStyle={{ width: "max-content" }}
  placeholder="Enter your phone number"
/>
                    {stepErrors.phone && (
                      <span className="wiz-field-err">{stepErrors.phone}</span>
                    )}
                    {phoneError && (
                      <span className="wiz-field-err">{phoneError}</span>
                    )}
                  </div> */}
                </div>
              )}

              <div className="wiz-card__footer sr-card-footer">
                {wizardStep > 0 ? (
                  <button
                    type="button"
                    className="wiz-btn wiz-btn--ghost"
                    onClick={prevStep}
                  >
                    <ChevronLeft size={15} />
                    Back
                  </button>
                ) : (
                  <div />
                )}

                {wizardStep < 2 ? (
                  <button
                    type="button"
                    className="wiz-btn wiz-btn--primary"
                    onClick={nextStep}
                  >
                    Next
                    <ChevronRight size={15} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="wiz-btn wiz-btn--primary wiz-btn--submit"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit Request
                        <ChevronRight size={15} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      <BackgroundFooter />
    </>
  );
}

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box}
  body{font-family:'Inter',sans-serif;background:#f8f9fa;margin:0}

  /* ═══ SPLIT PANEL ═══ */
  .sp-layout{display:flex;height:calc(100vh - 60px);font-family:'Inter',sans-serif;background:#f8f9fa}
  .sp-sidebar{width:380px;min-width:380px;background:#fff;border-right:1px solid #e9ecef;display:flex;flex-direction:column;overflow:hidden}
  .sp-sidebar__header{padding:20px 16px 12px;border-bottom:1px solid #f0f0f0;flex-shrink:0}
  .sp-sidebar__title-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
  .sp-sidebar__title{font-size:1.15rem;font-weight:700;color:#212529;margin:0}
  .sp-new-btn{width:34px;height:34px;border-radius:8px;border:none;background:#024A82;color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .15s}
  .sp-new-btn:hover{background:#024A82}
  .sp-search{display:flex;align-items:center;gap:8px;background:#f8f9fa;border:1px solid #e9ecef;border-radius:8px;padding:8px 12px;margin-bottom:10px}
  .sp-search input{border:none;outline:none;font-size:13px;font-family:'Inter',sans-serif;flex:1;background:transparent;color:#212529}
  .sp-search input::placeholder{color:#adb5bd}
  .sp-filters{display:flex;gap:4px;flex-wrap:wrap}
  .sp-filter-btn{padding:4px 10px;border-radius:6px;border:1px solid transparent;background:transparent;font-size:11.5px;font-weight:500;cursor:pointer;color:#6b7280;transition:all .15s;font-family:'Inter',sans-serif;display:inline-flex;align-items:center;gap:4px}
  .sp-filter-btn:hover{background:#f3f4f6}
  .sp-filter-btn--active{background:#eef2ff;color:#0d6efd;font-weight:600}
  .sp-filter-count{font-size:10px;font-weight:700;background:rgba(0,0,0,.06);padding:1px 5px;border-radius:8px}
  .sp-filter-btn--active .sp-filter-count{background:rgba(13,110,253,.12);color:#0d6efd}
  .sp-sidebar__list{flex:1;overflow-y:auto;padding:6px 0}
  .sp-ticket-row{padding:12px 16px;border-bottom:1px solid #f5f5f5;cursor:pointer;transition:all .12s}
  .sp-ticket-row:hover{background:#f8fafc}
  .sp-ticket-row--active{background:#eef2ff}
  .sp-ticket-row__top{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px}
  .sp-ticket-row__status{display:flex;align-items:center;gap:6px}
  .sp-status-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
  .sp-ticket-id{font-family:'SF Mono','Fira Code',monospace;font-size:11.5px;color:#6b7280;background:transparent;padding:0}
  .sp-ticket-row__time{font-size:11px;color:#adb5bd}
  .sp-ticket-row__subject{font-size:13.5px;font-weight:600;color:#212529;margin:0 0 4px;line-height:1.35;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .sp-ticket-row__preview{font-size:12px;color:#9ca3af;margin:0 0 6px;line-height:1.4;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .sp-preview-author{font-weight:600;color:#6b7280}
  .sp-ticket-row__meta{display:flex;justify-content:space-between;align-items:center}
  .sp-ticket-row__type{font-size:11px;color:#adb5bd;font-weight:500}
  .sp-ticket-row__badges{display:flex;gap:4px;align-items:center}
  .sp-mini-pill{padding:2px 7px;border-radius:4px;font-size:10px;font-weight:600;line-height:1}
  .sp-msg-count{display:inline-flex;align-items:center;gap:3px;font-size:10.5px;color:#9ca3af}
  .sp-loading{display:flex;align-items:center;justify-content:center;gap:10px;padding:40px 16px;color:#6b7280;font-size:13px}
  .sp-list-error{display:flex;flex-direction:column;align-items:center;gap:8px;padding:30px 16px;color:#842029;font-size:13px;text-align:center}
  .sp-retry{border:1px solid #dee2e6;background:#fff;border-radius:6px;padding:4px 12px;font-size:12px;cursor:pointer;color:#495057;font-family:'Inter',sans-serif;margin-top:4px}
  .sp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px 20px;text-align:center}
  .sp-empty p{color:#9ca3af;font-size:13px;margin:8px 0 0}
  .sp-empty-cta{margin-top:12px;border:none;background:#0d6efd;color:#fff;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif}
  .sp-main{flex:1;display:flex;flex-direction:column;overflow:hidden;background:#fafbfc}
  .sp-welcome{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:40px}
  .sp-welcome__icon{width:72px;height:72px;border-radius:16px;background:#eef2ff;display:flex;align-items:center;justify-content:center;margin-bottom:20px}
  .sp-welcome__title{font-size:1.25rem;font-weight:700;color:#212529;margin:0 0 8px}
  .sp-welcome__desc{font-size:14px;color:#6b7280;max-width:340px;margin:0 0 20px;line-height:1.5}
  .sp-welcome__cta{display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:#024A82;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;transition:background .15s}
  .sp-welcome__cta:hover{background:#024A82}
  .sp-chat-header{padding:14px 20px;background:#fff;border-bottom:1px solid #e9ecef;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-shrink:0}
  .sp-chat-header__left{display:flex;align-items:flex-start;gap:10px;flex:1;min-width:0}
  .sp-back-mobile{display:none;border:none;background:transparent;padding:4px;cursor:pointer;color:#495057;margin-top:2px}
  .sp-chat-header__badges{display:flex;align-items:center;gap:6px;margin-bottom:4px;flex-wrap:wrap}
  .sp-chat-header__subject{font-size:1rem;font-weight:700;color:#212529;margin:0 0 3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .sp-chat-header__meta{display:flex;align-items:center;gap:6px;font-size:12px;color:#9ca3af;flex-wrap:wrap}
  .sp-meta-sep{color:#d1d5db}
  .sp-chat-header__actions{display:flex;gap:6px;flex-shrink:0}
  .sp-info-bar{display:flex;gap:0;background:#fff;border-bottom:1px solid #f0f0f0;padding:0;flex-shrink:0}
  .sp-info-bar__item{flex:1;padding:10px 16px;border-right:1px solid #f0f0f0;display:flex;flex-direction:column;gap:2px}
  .sp-info-bar__item:last-child{border-right:none}
  .sp-info-bar__label{font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;color:#adb5bd}
  .sp-info-bar__value{font-size:12px;font-weight:500;color:#343a40}
  .sp-tags{display:flex;align-items:center;gap:6px;padding:8px 20px;border-bottom:1px solid #f0f0f0;background:#fff;flex-shrink:0}
  .sp-messages{flex:1;overflow-y:auto;padding:20px;display:flex;flex-direction:column;gap:16px}
  .sp-reply{padding:12px 16px;border-top:1px solid #e9ecef;background:#fff;flex-shrink:0}
  .sp-reply-error{display:flex;align-items:center;gap:6px;color:#842029;font-size:12.5px;margin-bottom:8px}
  .sp-reply__wrap{border:1px solid #dee2e6;border-radius:10px;overflow:hidden;transition:border-color .15s}
  .sp-reply__wrap:focus-within{border-color:#0d6efd}
  .sp-reply__wrap textarea{width:100%;border:none;padding:12px 14px;font-family:'Inter',sans-serif;font-size:13.5px;resize:none;outline:none;background:#fff;color:#212529}
  .sp-reply__wrap {
  position: relative;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  overflow: hidden;
  transition:
    border-color .15s,
    box-shadow .15s,
    background-color .15s;
}

.sp-reply__wrap--dragging {
  border-color: #0d6efd;
  box-shadow: 0 0 0 4px rgba(13, 110, 253, .1);
}

.sp-reply__drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(238, 246, 255, .96);
  color: #0d6efd;
  font-size: 13px;
  font-weight: 600;
  pointer-events: none;
}
  .sp-reply__footer{display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:#f8f9fa;border-top:1px solid #f0f0f0}
  .sp-reply__hint{font-size:11px;color:#adb5bd}
  .sp-reply__footer-left{display:flex;align-items:center;gap:8px;min-width:0}
  .attach-btn{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;border:1px solid #dee2e6;background:#fff;color:#6b7280;cursor:pointer;transition:all .15s;flex-shrink:0}
  .attach-btn:hover:not(:disabled){background:#eef2ff;color:#0d6efd;border-color:#0d6efd}
  .attach-btn:disabled{opacity:.5;cursor:not-allowed}
  .sp-reply__file{display:flex;align-items:center;gap:8px;padding:8px 12px;background:#f8f9fa;border-top:1px solid #f0f0f0;font-size:12.5px;color:#495057}
  .sp-reply__file-name{font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0}
  .sp-reply__file-size{font-size:11px;color:#adb5bd;flex-shrink:0}
  .sp-reply__file-x{border:none;background:transparent;color:#9ca3af;cursor:pointer;padding:2px;display:flex;align-items:center;border-radius:4px;flex-shrink:0}
  .sp-reply__file-x:hover:not(:disabled){background:#e9ecef;color:#dc3545}
  .attach-chip{display:inline-flex;align-items:center;gap:6px;margin-top:6px;padding:6px 10px;border-radius:8px;border:1px solid #dee2e6;background:#fff;font-size:12px;font-weight:500;color:#0d6efd;cursor:pointer;font-family:'Inter',sans-serif;max-width:100%;transition:all .15s}
  .attach-chip:hover:not(:disabled){background:#eef2ff;border-color:#0d6efd}
  .attach-chip:disabled{opacity:.6;cursor:wait}
  .attach-chip__name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:220px}
  .attach-chip__size{font-size:10.5px;color:#adb5bd;flex-shrink:0}

  /* ═══ SHARED ═══ */
  .tag-pill{background:#f3f4f6;color:#6b7280;border-radius:20px;padding:2px 10px;font-size:11.5px;font-weight:500}
  .sys-msg{display:flex;align-items:center;gap:8px;background:#f0fdf4;color:#166534;border:1px solid #dcfce7;border-radius:8px;padding:10px 14px;font-size:13px;font-weight:500}
  .bubble-row{display:flex;gap:10px;align-items:flex-end}
  .bubble-row--right{flex-direction:row-reverse}
  .b-avatar{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0;text-transform:uppercase}
  .b-avatar--you{background:#dbeafe;color:#1d4ed8}
  .b-avatar--agent{background:#ede9fe;color:#5b21b6}
  .b-content{display:flex;flex-direction:column;max-width:72%}
  .bubble-row--right .b-content{align-items:flex-end}
  .b-meta{display:flex;align-items:center;gap:6px;margin-bottom:4px;flex-wrap:wrap}
  .b-meta--right{flex-direction:row-reverse}
  .b-name{font-weight:600;font-size:12px;color:#343a40}
  .agent-pill{background:#ede9fe;color:#5b21b6;font-size:10px;font-weight:600;padding:1px 7px;border-radius:20px}
  .b-time{font-size:11px;color:#adb5bd}
  .bubble{padding:10px 14px;border-radius:14px;font-size:13.5px;line-height:1.6;white-space:pre-wrap;word-break:break-word}
  .bubble--you{background:#eff6ff;border:1px solid #bfdbfe;border-bottom-right-radius:4px}
  .bubble--agent{background:#f5f3ff;border:1px solid #ddd6fe;border-bottom-left-radius:4px}
  /* Clickable links inside support chat */
.bubble .sp-message-link {
  color: #0369a1 !important;
  text-decoration: underline !important;
  text-underline-offset: 2px;
  font-weight: 500;
  cursor: pointer;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.bubble .sp-message-link:hover {
  color: #024a82 !important;
  text-decoration-thickness: 2px;
}

.bubble .sp-message-link:focus-visible {
  outline: 2px solid #0369a1;
  outline-offset: 2px;
  border-radius: 2px;
}
  .b-ts{font-size:10px;color:#ced4da;margin-top:3px}
  .b-ts--right{text-align:right}
  .closed-bar{display:flex;align-items:center;gap:8px;padding:13px 16px;background:#f0fdf4;color:#166534;font-size:13px;font-weight:500;border-top:1px solid #dcfce7;flex-shrink:0}
  .send-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 16px;background:#024A82;color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;transition:background .15s;font-family:'Inter',sans-serif}
  .send-btn:hover:not(:disabled){background:#024A82}
  .send-btn:disabled{opacity:.55;cursor:not-allowed}
  .hdr-btn{display:inline-flex;align-items:center;gap:5px;padding:6px 12px;border-radius:8px;border:1px solid #dee2e6;background:#fff;font-size:12.5px;font-weight:500;cursor:pointer;color:#495057;transition:all .15s;font-family:'Inter',sans-serif}
  .hdr-btn:hover{background:#f8f9fa}
  .hdr-btn--blue{background:#024A82;color:#fff;border-color:#0d6efd}
  .hdr-btn--blue:hover{background:#024A82}

  /* ═══ WIZARD FORM ═══ */
  .wiz-page{font-family:'Inter',sans-serif;min-height:calc(100vh - 60px);background:#f8f9fa;padding-bottom:40px}

  .wiz-topbar{display:flex;align-items:center;gap:16px;padding:16px 24px;background:#fff;border-bottom:1px solid #e9ecef}
  .wiz-topbar__title{font-size:1.1rem;font-weight:700;color:#212529;margin:0}
  .wiz-back{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:8px;border:1px solid #dee2e6;background:#fff;font-size:13px;font-weight:500;cursor:pointer;color:#495057;transition:all .15s;font-family:'Inter',sans-serif}
  .wiz-back:hover{background:#f0f0f0;border-color:#adb5bd}

  /* Progress bar */
  .wiz-progress{display:flex;align-items:center;justify-content:center;padding:28px 24px 20px;gap:0;max-width:560px;margin:0 auto}
  .wiz-step{display:flex;align-items:center;gap:8px;position:relative}
  .wiz-step__circle{width:32px;height:32px;border-radius:50%;border:2px solid #dee2e6;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#adb5bd;background:#fff;transition:all .2s;flex-shrink:0}
  .wiz-step--active .wiz-step__circle{border-color:#024A82;background:#024A82;color:#fff}
  .wiz-step--done .wiz-step__circle{border-color:#024A82;background:#024A82;color:#fff}
  .wiz-step__label{font-size:12.5px;font-weight:500;color:#adb5bd;white-space:nowrap;transition:color .2s}
  .wiz-step--active .wiz-step__label{color:#212529;font-weight:600}
  .wiz-step--done .wiz-step__label{color:#024A82}
  .wiz-step__line{width:48px;height:2px;background:#dee2e6;margin:0 12px;flex-shrink:0;transition:background .2s}
  .wiz-step__line--done{background:#024A82}

  /* Card */
  .wiz-card{max-width:640px;margin:0 auto;background:#fff;border:1px solid #e9ecef;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,.05);overflow:hidden}
  .wiz-card__body{padding:28px 32px 20px}
  .wiz-card__heading{font-size:1.1rem;font-weight:700;color:#212529;margin:0 0 4px}
  .wiz-card__sub{font-size:13.5px;color:#6b7280;margin:0 0 24px;line-height:1.5}

  .wiz-card__footer{display:flex;align-items:center;justify-content:space-between;padding:16px 32px;border-top:1px solid #f0f0f0;background:#fafbfc}

  /* Buttons */
  .wiz-btn{display:inline-flex;align-items:center;gap:6px;padding:9px 20px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
  .wiz-btn--primary{background:#0d6efd;color:#fff;border:none}
  .wiz-btn--primary:hover:not(:disabled){background:#0b5ed7;transform:translateY(-1px);box-shadow:0 4px 12px rgba(13,110,253,.25)}
  .wiz-btn--primary:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none}
  .wiz-btn--submit{padding:10px 28px}
  .wiz-btn--ghost{background:transparent;color:#495057;border:1px solid #dee2e6}
  .wiz-btn--ghost:hover{background:#f3f4f6;border-color:#adb5bd}

  /* Fields */
  .wiz-field{margin-bottom:20px}
  .wiz-field--half{flex:1;min-width:0}
  .wiz-row{display:flex;gap:16px}
  .wiz-label{display:block;font-size:13px;font-weight:600;color:#343a40;margin-bottom:6px}
  .req{color:#dc3545}
  .wiz-input,.wiz-select,.wiz-textarea{width:100%;padding:10px 14px;border:1px solid #dee2e6;border-radius:8px;font-size:14px;font-family:'Inter',sans-serif;color:#212529;transition:border-color .15s,box-shadow .15s;background:#fff;outline:none}
  .wiz-input:focus,.wiz-select:focus,.wiz-textarea:focus{border-color:#0d6efd;box-shadow:0 0 0 3px rgba(13,110,253,.1)}
  .wiz-input--err{border-color:#dc3545!important;box-shadow:0 0 0 3px rgba(220,53,69,.1)!important}
  .wiz-textarea{resize:vertical;line-height:1.6}
  .wiz-select{appearance:auto;cursor:pointer}
  .wiz-field-err{display:block;font-size:12px;color:#dc3545;margin-top:4px;font-weight:500}
  .wiz-field-ok{display:flex;align-items:center;gap:4px;font-size:12px;color:#166534;margin-top:4px;font-weight:500}

  /* Priority chips */
  .wiz-priority-row{display:flex;gap:8px;flex-wrap:wrap}
  .wiz-pri-chip{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:1.5px solid #dee2e6;background:#fff;font-size:13px;font-weight:600;cursor:pointer;color:#6b7280;transition:all .15s;font-family:'Inter',sans-serif}
  .wiz-pri-chip:hover{border-color:#adb5bd;background:#f8f9fa}
  .wiz-pri-chip--active{font-weight:700}
  .wiz-pri-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
  .wiz-pri-desc{display:flex;align-items:center;gap:6px;font-size:12.5px;color:#6b7280;margin-top:10px;padding:8px 12px;background:#f8f9fa;border-radius:6px}

  /* File upload zone */
 .wiz-file-zone {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 92px;
  padding: 20px;
  border: 2px dashed #dee2e6;
  border-radius: 10px;
  background: #fafbfc;
  transition:
    border-color .15s,
    background-color .15s,
    box-shadow .15s;
  cursor: pointer;
  outline: none;
}

.wiz-file-zone:hover,
.wiz-file-zone:focus {
  border-color: #0d6efd;
  background: #f8fbff;
}

.wiz-file-zone--dragging {
  border-color: #0d6efd;
  background: #eef6ff;
  box-shadow: 0 0 0 4px rgba(13, 110, 253, .1);
}

.wiz-file-zone--selected {
  border-style: solid;
  border-color: #bfdbfe;
  background: #f8fbff;
}

.wiz-file-zone__content {
  flex: 1;
  min-width: 0;
}

.wiz-file-text {
  display: block;
  font-size: 13.5px;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wiz-file-hint {
  display: block;
  margin-top: 3px;
  font-size: 11.5px;
  color: #adb5bd;
}

.wiz-file-remove {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wiz-file-remove:hover:not(:disabled) {
  background: #fee2e2;
  color: #dc3545;
}

.wiz-file-remove:disabled {
  opacity: .5;
  cursor: not-allowed;
}
  .wiz-file-text{font-size:13.5px;color:#495057;display:block}
  .wiz-file-link{color:#0d6efd;font-weight:600;cursor:pointer;text-decoration:underline}
  .wiz-file-hint{font-size:11.5px;color:#adb5bd;display:block;margin-top:2px}

  /* Review block */
  .wiz-review{background:#f8f9fa;border:1px solid #e9ecef;border-radius:10px;padding:16px 20px;margin-bottom:20px}
  .wiz-review__row{display:flex;justify-content:space-between;align-items:flex-start;padding:6px 0;border-bottom:1px solid #f0f0f0}
  .wiz-review__row:last-child{border-bottom:none}
  .wiz-review__label{font-size:12px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px;min-width:100px;flex-shrink:0}
  .wiz-review__value{font-size:13px;font-weight:500;color:#343a40;text-align:right;display:flex;align-items:center;gap:5px}
  .wiz-review__desc{max-width:360px;line-height:1.4;color:#6b7280}
  .wiz-autofill{display:flex;align-items:center;gap:8px;background:#f0fdf4;border:1px solid #dcfce7;border-radius:8px;padding:10px 14px;margin-bottom:20px;font-size:13px;color:#166534}
  .wiz-error{max-width:640px;margin:16px auto 0;background:#f8d7da;color:#842029;border-radius:8px;padding:12px 16px;display:flex;align-items:center;gap:8px;font-size:14px;font-weight:500}

  /* Phone input override */
  .wiz-phone-container{margin-bottom:0!important}

  @keyframes spin{to{transform:rotate(360deg)}}
  .spin{animation:spin .8s linear infinite;display:inline-block}

  /* ═══ RESPONSIVE ═══ */
  @media(max-width:768px){
    .sp-layout{flex-direction:column;height:auto;min-height:calc(100vh - 60px)}
    .sp-sidebar{width:100%;min-width:100%;max-height:none;border-right:none;border-bottom:1px solid #e9ecef}
    .sp-sidebar__list{max-height:50vh}
    .sp-main{min-height:60vh}
    .sp-back-mobile{display:block}
    .sp-info-bar{flex-wrap:wrap}
    .sp-info-bar__item{min-width:50%;border-bottom:1px solid #f0f0f0}
    .wiz-card__body{padding:20px 16px 16px}
    .wiz-card__footer{padding:14px 16px}
    .wiz-row{flex-direction:column;gap:0}
    .wiz-progress{padding:20px 16px 14px}
    .wiz-step__label{display:none}
    .wiz-step__line{width:32px;margin:0 6px}
    .wiz-pri-chip{padding:6px 12px;font-size:12px}
  }

  /* ═══════════════════════════════════════════════════════════════
   PROFESSIONAL SUPPORT REQUEST WIZARD REDESIGN
   Add this near the end of STYLES so it overrides old wizard CSS
   ═══════════════════════════════════════════════════════════════ */

.sr-page {
  min-height: calc(100vh - 60px);
  background:
    radial-gradient(circle at top left, rgba(3, 94, 165, 0.08), transparent 34%),
    linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%);
  padding-bottom: 46px;
  font-family: 'Inter', sans-serif;
}

.sr-topbar {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 28px;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid #e2e8f0;
  backdrop-filter: blur(10px);
}

.sr-back-btn {
  height: 38px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  color: #334155;
  font-weight: 700;
}

.sr-back-btn:hover {
  background: #eef6ff;
  border-color: #bfdbfe;
  color: #024A82;
}

.sr-page-title {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.sr-page-subtitle { 
  margin: 3px 0 0;
  color: #64748b;
  font-size: 12.5px;
}

.sr-shell {
  width: min(1180px, calc(100% - 48px));
  margin: 28px auto 0;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

.sr-side-panel {
  position: sticky;
  top: 18px;
  min-height: 640px;
  padding: 26px;
  border-radius: 18px !important ;
  color: #ffffff;
  background: rgb(3, 94, 165);
  box-shadow: 0 20px 45px rgba(2, 74, 130, 0.22);
  overflow: hidden;
}
.sr-side-kicker {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.20);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 22px;
}

.sr-side-title {
  position: relative;
  z-index: 1;
  margin: 0;
  max-width: 300px;
  font-size: 20px;
  line-height: 1.24;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.sr-side-text {
  position: relative;
  z-index: 1;
  max-width: 310px;
  margin: 14px 0 24px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  line-height: 1.65;
}

.sr-help-list {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 12px;
  margin-top: 18px;
}

.sr-help-item {
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 12px;
  align-items: start;
  padding: 13px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.11);
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.sr-help-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.16);
}

.sr-help-item strong {
  display: block;
  font-size: 13px;
  color: #ffffff;
  margin-bottom: 2px;
}

.sr-help-item span {
  display: block;
  color: rgba(255, 255, 255, 0.72);
  font-size: 11.5px;
  line-height: 1.45;
}

.sr-sla-card {
  position: relative;
  z-index: 1;
  margin-top: 22px;
  padding: 16px;
  border-radius: 16px;
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
}

.sr-sla-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.sr-sla-value {
  margin-top: 6px;
  font-size: 17px;
  font-weight: 700;
}

.sr-sla-desc {
  margin-top: 5px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.sr-main-panel {
  min-width: 0;
}

.sr-progress-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  padding: 14px 18px;
  margin-bottom: 16px;
}

.sr-progress {
  max-width: none;
  margin: 0;
  padding: 0;
  justify-content: space-between;
}

.sr-progress .wiz-step {
  flex: 1;
}

.sr-progress .wiz-step__line {
  flex: 1;
  width: auto;
  min-width: 34px;
  margin: 0 16px;
}

.sr-request-card {
  max-width: none;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.09);
  overflow: hidden;
}

.sr-card-header {
  margin-bottom: 22px;
}

.sr-step-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef6ff;
  color: #024A82;
  border: 1px solid #bfdbfe;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 12px;
}

.sr-request-card .wiz-card__body {
  padding: 30px 34px 24px;
}

.sr-request-card .wiz-card__heading {
  color: #0f172a;
  font-size: 21px;
  letter-spacing: -0.02em;
}

.sr-request-card .wiz-card__sub {
  color: #64748b;
  font-size: 13px;
  margin-bottom: 0;
}

.sr-request-card .wiz-label {
  color: #172033;
  font-size: 12.5px;
}

.sr-request-card .wiz-input,
.sr-request-card .wiz-select,
.sr-request-card .wiz-textarea {
  border-radius: 11px;
  border: 1px solid #dbe3ef;
  background: #ffffff;
  color: #0f172a;
}

.sr-request-card .wiz-input,
.sr-request-card .wiz-select {
  height: 44px;
}

.sr-request-card .wiz-input:focus,
.sr-request-card .wiz-select:focus,
.sr-request-card .wiz-textarea:focus {
  border-color: #024A82;
  box-shadow: 0 0 0 4px rgba(3, 94, 165, 0.10);
}

.sr-priority-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.sr-priority-card {
  min-height: 54px;
  justify-content: flex-start;
  border-radius: 13px;
  padding: 10px 12px;
  background: #ffffff;
}

.sr-priority-code {
  font-size: 13px;
  font-weight: 600;
}

.sr-priority-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.sr-priority-desc {
  margin-top: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 12px;
}

.sr-file-zone {
  border-radius: 16px;
  min-height: 108px;
  background: #f8fafc;
  border-color: #cbd5e1;
}

.sr-file-zone:hover,
.sr-file-zone:focus,
.wiz-file-zone--dragging {
  border-color: #024A82;
  background: #eef6ff;
  box-shadow: 0 0 0 4px rgba(3, 94, 165, 0.10);
}

.sr-upload-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sr-review-box {
  background: #f8fafc;
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  padding: 18px 20px;
}

.sr-review-box .wiz-review__row {
  padding: 9px 0;
}

.sr-autofill {
  border-radius: 12px;
  border: 1px solid #bbf7d0;
  background: #ecfdf5;
}

.sr-card-footer {
  padding: 18px 34px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.sr-request-card .wiz-btn {
  border-radius: 11px;
  min-height: 42px;
}

.sr-request-card .wiz-btn--primary {
  background: #024A82;
}

.sr-request-card .wiz-btn--primary:hover:not(:disabled) {
  background: #035EA5;
}

.sr-error {
  max-width: none;
  margin: 0 0 16px;
  border-radius: 14px;
}

/* Keep footer natural and avoid huge empty form feel */
.sr-page + footer,
.sr-page ~ footer {
  margin-top: 0;
}

/* Responsive */
@media (max-width: 992px) {
  .sr-shell {
    grid-template-columns: 1fr;
    width: min(760px, calc(100% - 32px));
  }

  .sr-side-panel {
    position: relative;
    top: 0;
    min-height: auto;
  }

  .sr-help-list {
    grid-template-columns: repeat(3, 1fr);
  }

  .sr-help-item {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .sr-topbar {
    align-items: flex-start;
    flex-direction: column;
    padding: 16px;
  }

  .sr-shell {
    width: calc(100% - 24px);
    margin-top: 16px;
  }

  .sr-help-list {
    grid-template-columns: 1fr;
  }

  .sr-progress {
    overflow-x: auto;
    justify-content: flex-start;
    gap: 12px;
  }

  .sr-progress .wiz-step {
    flex: 0 0 auto;
  }

  .sr-progress .wiz-step__label {
    display: inline;
  }

  .sr-progress .wiz-step__line {
    width: 34px;
    flex: 0 0 34px;
    margin: 0 4px;
  }

  .sr-request-card .wiz-card__body {
    padding: 22px 18px 18px;
  }

  .sr-card-footer {
    padding: 14px 18px;
  }

  .sr-priority-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* =========================================================
   FIX: Remove page scrollbar between navbar and footer
   Support content scrolls internally only
   ========================================================= */

body.support-page-scroll-lock {
  overflow: hidden !important;
}

/* Standalone customer portal layout */
body.support-page-scroll-lock .sp-layout {
height: calc(100vh - 78px) !important;  min-height: 0 !important;
  overflow: hidden !important;
}

/* If Support is inside SUSAN shell, keep existing column behavior */
body.support-page-scroll-lock .support-page-content > .sp-layout {
  height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

/* Left ticket list should scroll internally */
body.support-page-scroll-lock .sp-sidebar {
  height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

body.support-page-scroll-lock .sp-sidebar__list {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

/* Right panel should not create page scroll */
body.support-page-scroll-lock .sp-main {
  height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

/* Chat messages scroll internally */
body.support-page-scroll-lock .sp-messages {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

/* New request page should scroll inside content area, not body */
body.support-page-scroll-lock .sr-page {
  height: calc(100vh - 78px) !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

body.support-page-scroll-lock .support-page-content > .sr-page {
  height: 100% !important;
}

/* ═══════════════════════════════════════════════════════════════
   CHAT AREA TABS: Conversation + Details
   ═══════════════════════════════════════════════════════════════ */

.sp-ticket-tabs {
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 0 20px;
  height: 48px;
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.sp-ticket-tab {
  position: relative;
  height: 48px;
  border: none;
  background: transparent;
  color: #64748b;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 2px;
}

.sp-ticket-tab:hover {
  color: #024A82;
}

.sp-ticket-tab--active {
  color: #024A82;
}

.sp-ticket-tab--active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 3px;
  border-radius: 999px 999px 0 0;
  background: #024A82;
}

.sp-details-tab {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 20px 24px;
  background: #f8fafc;
}

.sp-details-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.sp-detail-card {
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.sp-detail-card--wide {
  grid-column: 1 / -1;
}

.sp-detail-card__header {
  padding: 16px 18px;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.sp-detail-card__header h5 {
  margin: 0;
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
}

.sp-detail-card__header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.sp-detail-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: 4px 9px;
  border-radius: 999px;
  background: #eef6ff;
  border: 1px solid #bfdbfe;
  color: #024A82;
  font-size: 11px;
}

.sp-detail-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

.sp-detail-row {
  min-height: 66px;
  padding: 13px 16px;
  border-bottom: 1px solid #edf2f7;
  border-right: 1px solid #edf2f7;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.sp-detail-row:nth-child(2n) {
  border-right: none;
}

.sp-detail-row span {
  color: #94a3b8;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.sp-detail-row strong {
  color: #0f172a;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sp-attachment-list {
  padding: 10px 14px 14px;
  display: grid;
  gap: 10px;
}

.sp-attachment-row {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 11px 12px;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  background: #ffffff;
}

.sp-attachment-icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef6ff;
  color: #024A82;
}

.sp-attachment-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sp-attachment-meta strong {
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sp-attachment-meta span {
  color: #64748b;
  font-size: 11.5px;
}

.sp-attachment-download {
  border: 1px solid #bfdbfe;
  background: #eef6ff;
  color: #024A82;
  border-radius: 9px;
  padding: 7px 11px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.sp-attachment-download:hover:not(:disabled) {
  background: #024A82;
  color: #ffffff;
}

.sp-attachment-download:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.sp-detail-empty {
  min-height: 116px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #94a3b8;
  font-size: 13px;
}

.sp-activity-list {
  padding: 12px 18px 18px;
  display: grid;
  gap: 0;
}

.sp-activity-item {
  position: relative;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 12px;
  padding: 10px 0;
}

.sp-activity-item:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 16px;
  top: 42px;
  bottom: -8px;
  width: 1px;
  background: #dbe3ef;
}

.sp-activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef6ff;
  color: #024A82;
  position: relative;
  z-index: 1;
}

.sp-activity-icon--customer {
  background: #ecfdf5;
  color: #047857;
}

.sp-activity-icon--support {
  background: #ede9fe;
  color: #5b21b6;
}

.sp-activity-icon--closed {
  background: #dcfce7;
  color: #166534;
}

.sp-activity-content {
  min-width: 0;
  padding-bottom: 8px;
}

.sp-activity-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sp-activity-top strong {
  color: #0f172a;
  font-size: 13px;
}

.sp-activity-top span {
  color: #94a3b8;
  font-size: 11px;
  white-space: nowrap;
}

.sp-activity-content p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

@media (max-width: 992px) {
  .sp-details-grid {
    grid-template-columns: 1fr;
  }

  .sp-detail-card--wide {
    grid-column: auto;
  }

  .sp-detail-list {
    grid-template-columns: 1fr;
  }

  .sp-detail-row {
    border-right: none;
  }
}

/* =========================================================
   Compact footer for Support Portal
   ========================================================= */

body.support-page-scroll-lock .footer {
  height: 28px !important;
  min-height: 28px !important;
  padding: 0 14px !important;
  display: flex !important;
  align-items: center !important;
}

body.support-page-scroll-lock .footertextbottom {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 12px !important;
  line-height: 28px !important;
}

body.support-page-scroll-lock #Rights {
  font-size: 12px !important;
}


/* =========================================================
   Fix support left card icons and remove bottom circle
   ========================================================= */

.sr-help-item .sr-help-icon {
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  min-height: 38px !important;

  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  padding: 0 !important;
  margin: 0 !important;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  line-height: 1 !important;
}

.sr-help-item .sr-help-icon svg {
  display: block;
  width: 17px;
  height: 17px;
  margin: 0 !important;
  padding: 0 !important;
  flex-shrink: 0;
}

/* Keep normal text spans unaffected */
.sr-help-item > div span {
  display: block;
}

/* Remove bottom-right decorative circle */
.sr-side-panel::after {
  display: none !important;
  content: none !important;
}

/* =========================================================
   FINAL: Support Request viewport fit
   Goal:
   - No page scrollbar on desktop/laptop
   - Step 1, Step 2, Step 3 fit inside screen
   - Next / Back / Submit buttons always visible
   - Keep existing design, only compact height
   ========================================================= */

/* Left ServQual Support card background only */
.sr-side-panel {
  background: rgb(3, 94, 165) !important;
}

/* Remove only old decorative overlays */
.sr-side-panel::before,
.sr-side-panel::after {
  display: none !important;
  content: none !important;
}

/* Desktop and laptop fit */
@media (min-width: 993px) {
  body.support-page-scroll-lock {
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-page {
    height: calc(100dvh - 78px) !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding-bottom: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }

  body.support-page-scroll-lock .sr-topbar {
    flex: 0 0 auto !important;
    min-height: 62px !important;
    padding: 10px 28px !important;
  }

  body.support-page-scroll-lock .sr-page-title {
    font-size: 17px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-page-subtitle {
    font-size: 12px !important;
    line-height: 1.3 !important;
  }

  body.support-page-scroll-lock .sr-shell {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    width: min(1180px, calc(100% - 48px)) !important;
    margin: 14px auto 8px !important;
    display: grid !important;
    grid-template-columns: 360px minmax(0, 1fr) !important;
    gap: 22px !important;
    align-items: stretch !important;
  }

  body.support-page-scroll-lock .sr-side-panel {
    position: relative !important;
    top: auto !important;
    height: 100% !important;
    min-height: 0 !important;
    max-height: none !important;
    padding: 22px 24px !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 16px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 19px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 10px 0 16px !important;
    font-size: 12.5px !important;
    line-height: 1.45 !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 9px !important;
    margin-top: 0 !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 56px !important;
    padding: 10px !important;
  }

  body.support-page-scroll-lock .sr-help-icon {
    width: 34px !important;
    height: 34px !important;
    min-width: 34px !important;
    min-height: 34px !important;
  }

  body.support-page-scroll-lock .sr-help-item strong {
    font-size: 12px !important;
    line-height: 1.25 !important;
  }

  body.support-page-scroll-lock .sr-help-item > div span {
    font-size: 10.8px !important;
    line-height: 1.3 !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    margin-top: auto !important;
    padding: 13px !important;
  }

  body.support-page-scroll-lock .sr-sla-value {
    font-size: 16px !important;
  }

  body.support-page-scroll-lock .sr-sla-desc {
    font-size: 11.5px !important;
    line-height: 1.35 !important;
  }

  body.support-page-scroll-lock .sr-main-panel {
    height: 100% !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-progress-card {
    flex: 0 0 auto !important;
    padding: 11px 18px !important;
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-progress .wiz-step__circle {
    width: 30px !important;
    height: 30px !important;
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .sr-progress .wiz-step__label {
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .sr-request-card {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: none !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 20px 34px 12px !important;
  }

  body.support-page-scroll-lock .sr-card-header {
    margin-bottom: 14px !important;
  }

  body.support-page-scroll-lock .sr-step-badge {
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__heading {
    font-size: 19px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__sub {
    font-size: 12.2px !important;
    line-height: 1.35 !important;
  }


  body.support-page-scroll-lock .wiz-field {
    margin-bottom: 11px !important;
  }

  body.support-page-scroll-lock .wiz-label {
    margin-bottom: 5px !important;
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-input,
  body.support-page-scroll-lock .sr-request-card .wiz-select {
    height: 40px !important;
    min-height: 40px !important;
    padding: 8px 12px !important;
    font-size: 13px !important;
  }

  body.support-page-scroll-lock .sr-priority-grid {
    gap: 8px !important;
  }

  body.support-page-scroll-lock .sr-priority-card {
    min-height: 44px !important;
    padding: 8px 10px !important;
  }

  body.support-page-scroll-lock .sr-priority-desc {
    margin-top: 8px !important;
    padding: 8px 10px !important;
    font-size: 11.8px !important;
  }

  body.support-page-scroll-lock .sr-card-footer {
    flex: 0 0 auto !important;
    padding: 10px 34px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-btn {
    min-height: 38px !important;
    padding: 8px 18px !important;
  }

  /* Step 2 fit */
  body.support-page-scroll-lock .sr-request-card .wiz-textarea {
    height: clamp(116px, 22vh, 160px) !important;
    min-height: 116px !important;
    resize: none !important;
    font-size: 13px !important;
    line-height: 1.45 !important;
  }

  body.support-page-scroll-lock .sr-file-zone {
    min-height: clamp(76px, 13vh, 94px) !important;
    padding: 14px !important;
  }

  body.support-page-scroll-lock .sr-upload-icon {
    width: 38px !important;
    height: 38px !important;
  }

  /* Step 3 fit */
  body.support-page-scroll-lock .sr-review-box {
    padding: 10px 16px !important;
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-review-box .wiz-review__row {
    padding: 5px 0 !important;
  }

  body.support-page-scroll-lock .wiz-review__label {
    font-size: 10.5px !important;
  }

  body.support-page-scroll-lock .wiz-review__value {
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .sr-autofill {
    padding: 8px 12px !important;
    margin-bottom: 10px !important;
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .wiz-phone-container input {
    height: 40px !important;
  }
}

/* Extra compact for MacBook / short laptop screens */
@media (min-width: 993px) and (max-height: 760px) {
  body.support-page-scroll-lock .sr-topbar {
    min-height: 52px !important;
    padding: 7px 28px !important;
  }

  body.support-page-scroll-lock .sr-page-subtitle {
    display: none !important;
  }

  body.support-page-scroll-lock .sr-shell {
    margin: 8px auto 6px !important;
    gap: 18px !important;
  }

  body.support-page-scroll-lock .sr-side-panel {
    padding: 16px 20px !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 10px !important;
    padding: 4px 9px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 17px !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 7px 0 10px !important;
    font-size: 11.5px !important;
    line-height: 1.35 !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 6px !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 48px !important;
    padding: 7px !important;
  }

  body.support-page-scroll-lock .sr-help-icon {
    width: 30px !important;
    height: 30px !important;
    min-width: 30px !important;
    min-height: 30px !important;
  }

  body.support-page-scroll-lock .sr-help-icon svg {
    width: 15px !important;
    height: 15px !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    padding: 10px !important;
  }

  body.support-page-scroll-lock .sr-sla-title {
    font-size: 10px !important;
  }

  body.support-page-scroll-lock .sr-sla-value {
    font-size: 15px !important;
    margin-top: 4px !important;
  }

  body.support-page-scroll-lock .sr-sla-desc {
    font-size: 10.8px !important;
  }

  body.support-page-scroll-lock .sr-progress-card {
    padding: 8px 14px !important;
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .sr-progress .wiz-step__circle {
    width: 28px !important;
    height: 28px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    padding: 14px 28px 8px !important;
  }

  body.support-page-scroll-lock .sr-card-header {
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-step-badge {
    margin-bottom: 6px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__heading {
    font-size: 17px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__sub {
    font-size: 11.5px !important;
  }

  body.support-page-scroll-lock .wiz-field {
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-input,
  body.support-page-scroll-lock .sr-request-card .wiz-select {
    height: 36px !important;
    min-height: 36px !important;
  }

  body.support-page-scroll-lock .sr-priority-card {
    min-height: 38px !important;
  }

  body.support-page-scroll-lock .sr-priority-desc {
    margin-top: 6px !important;
    padding: 6px 9px !important;
    font-size: 11px !important;
  }

  body.support-page-scroll-lock .sr-card-footer {
    padding: 8px 28px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-btn {
    min-height: 34px !important;
    padding: 7px 16px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-textarea {
    height: 104px !important;
    min-height: 104px !important;
  }

  body.support-page-scroll-lock .sr-file-zone {
    min-height: 70px !important;
    padding: 10px !important;
  }

  body.support-page-scroll-lock .sr-review-box {
    padding: 8px 14px !important;
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .sr-autofill {
    padding: 7px 10px !important;
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .wiz-phone-container input {
    height: 36px !important;
  }
}

/* Mobile/tablet should scroll naturally because content stacks */
@media (max-width: 992px) {
  body.support-page-scroll-lock {
    overflow: auto !important;
  }

  body.support-page-scroll-lock .sr-page {
    height: auto !important;
    min-height: calc(100dvh - 78px) !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  body.support-page-scroll-lock .sr-shell {
    align-items: start !important;
  }

  body.support-page-scroll-lock .sr-side-panel,
  body.support-page-scroll-lock .sr-request-card {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    overflow: visible !important;
  }
}
  

/* =========================================================
   FINAL CLEAN FIT:
   - Left card natural height
   - Step/form card natural height
   - Aligned from top
   - Button visible
   - No full-height stretching
   ========================================================= */

@media (min-width: 993px) {
  body.support-page-scroll-lock .sr-page {
    height: calc(100dvh - 78px) !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding-bottom: 0 !important;
  }

  body.support-page-scroll-lock .sr-shell {
    width: min(1180px, calc(100% - 48px)) !important;
    margin: 14px auto 0 !important;
    display: grid !important;
    grid-template-columns: 360px minmax(0, 1fr) !important;
    gap: 22px !important;
    align-items: start !important;
  }

  /* Left card should NOT stretch full page height */
  body.support-page-scroll-lock .sr-side-panel {
    position: relative !important;
    top: auto !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: calc(100dvh - 178px) !important;
    padding: 22px 24px !important;
    overflow: hidden !important;
  }

  /* Keep selected priority close, not pushed too far down */
  body.support-page-scroll-lock .sr-sla-card {
    margin-top: 18px !important;
  }

  /* Right side should align with left card from top */
  body.support-page-scroll-lock .sr-main-panel {
    height: auto !important;
    min-height: 0 !important;
    display: block !important;
    overflow: visible !important;
  }

  /* Stepper should be compact, not tall */
  body.support-page-scroll-lock .sr-progress-card {
    height: auto !important;
    padding: 11px 18px !important;
    margin-bottom: 12px !important;
  }

  body.support-page-scroll-lock .sr-progress {
    padding: 0 !important;
  }

  /* Form card should be natural height, not full-height */
  body.support-page-scroll-lock .sr-request-card {
    height: auto !important;
    min-height: 0 !important;
    max-height: calc(100dvh - 210px) !important;
    display: block !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    height: auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 20px 34px 12px !important;
  }

  body.support-page-scroll-lock .sr-card-footer {
    padding: 10px 34px !important;
  }
}

/* Step 2 compact enough so upload + Next button stay visible */
@media (min-width: 993px) and (max-height: 850px) {
  body.support-page-scroll-lock .sr-topbar {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-card-header {
    margin-bottom: 12px !important;
  }

  body.support-page-scroll-lock .wiz-field {
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-textarea {
    height: 140px !important;
    min-height: 140px !important;
    resize: none !important;
  }

  body.support-page-scroll-lock .sr-file-zone {
    min-height: 82px !important;
    padding: 12px 14px !important;
  }

  body.support-page-scroll-lock .sr-upload-icon {
    width: 38px !important;
    height: 38px !important;
  }
}

/* Extra compact MacBook height */
@media (min-width: 993px) and (max-height: 760px) {
  body.support-page-scroll-lock .sr-page-subtitle {
    display: none !important;
  }

  body.support-page-scroll-lock .sr-shell {
    margin-top: 10px !important;
  }

  body.support-page-scroll-lock .sr-side-panel {
    max-height: calc(100dvh - 154px) !important;
    padding: 18px 20px !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 17px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 8px 0 10px !important;
    font-size: 11.5px !important;
    line-height: 1.35 !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 7px !important;
    margin-top: 0 !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    padding: 8px !important;
    min-height: 48px !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    margin-top: 12px !important;
    padding: 10px !important;
  }

  body.support-page-scroll-lock .sr-progress-card {
    padding: 8px 14px !important;
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .sr-request-card {
    max-height: calc(100dvh - 184px) !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    padding: 14px 28px 8px !important;
  }

  body.support-page-scroll-lock .sr-card-footer {
    padding: 8px 28px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-textarea {
    height: 104px !important;
    min-height: 104px !important;
  }

  body.support-page-scroll-lock .sr-file-zone {
    min-height: 70px !important;
    padding: 10px !important;
  }
}

/* =========================================================
   FINAL LEFT SUPPORT CARD HEIGHT FIX
   Only fixes left card height + spacing
   Does not touch right form, stepper, buttons or functionality
   ========================================================= */

@media (min-width: 993px) {
  body.support-page-scroll-lock .sr-side-panel {
    height: clamp(540px, calc(100dvh - 235px), 610px) !important;
    min-height: 540px !important;
    max-height: calc(100dvh - 150px) !important;

    padding: 26px !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 20px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 20px !important;
    line-height: 1.24 !important;
    max-width: 310px !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 14px 0 22px !important;
    font-size: 13px !important;
    line-height: 1.55 !important;
    max-width: 320px !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 12px !important;
    margin-top: 0 !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 64px !important;
    padding: 13px !important;
    grid-template-columns: 38px 1fr !important;
    gap: 12px !important;
    align-items: center !important;
  }

  body.support-page-scroll-lock .sr-help-icon {
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
    min-height: 38px !important;
  }

  body.support-page-scroll-lock .sr-help-item strong {
    font-size: 13px !important;
    line-height: 1.25 !important;
    margin-bottom: 3px !important;
  }

  body.support-page-scroll-lock .sr-help-item > div span {
    font-size: 11.5px !important;
    line-height: 1.4 !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    margin-top: auto !important;
    padding: 16px !important;
    flex-shrink: 0 !important;
  }

  body.support-page-scroll-lock .sr-sla-title {
    font-size: 11px !important;
  }

  body.support-page-scroll-lock .sr-sla-value {
    margin-top: 6px !important;
    font-size: 17px !important;
  }

  body.support-page-scroll-lock .sr-sla-desc {
    margin-top: 6px !important;
    font-size: 12px !important;
    line-height: 1.45 !important;
  }
}

/* MacBook height: keep card balanced, not too tall, not compressed */
@media (min-width: 993px) and (max-height: 820px) {
  body.support-page-scroll-lock .sr-side-panel {
    height: clamp(500px, calc(100dvh - 170px), 570px) !important;
    min-height: 500px !important;
    padding: 22px !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 16px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 19px !important;
    line-height: 1.22 !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 12px 0 18px !important;
    font-size: 12.5px !important;
    line-height: 1.45 !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 10px !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 58px !important;
    padding: 11px !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    padding: 14px !important;
  }
}

/* Very short screens */
@media (min-width: 993px) and (max-height: 720px) {
  body.support-page-scroll-lock .sr-side-panel {
    height: calc(100dvh - 155px) !important;
    min-height: 0 !important;
    padding: 18px 20px !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 17px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 8px 0 10px !important;
    font-size: 11.5px !important;
    line-height: 1.35 !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 7px !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 48px !important;
    padding: 8px !important;
  }

  body.support-page-scroll-lock .sr-help-icon {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    padding: 10px !important;
  }
}


/* =========================================================
   FINAL SUPPORT LAYOUT FIX
   Purpose:
   1. Left and right sections stay visually aligned
   2. Step 3 buttons remain visible even at 150% zoom
   3. No full page scrollbar on desktop
   4. Do not change existing design, only layout behaviour
   ========================================================= */

.sr-side-panel {
  background: rgb(3, 94, 165) !important;
}

.sr-side-panel::before,
.sr-side-panel::after {
  display: none !important;
  content: none !important;
}

@media (min-width: 993px) {
  body.support-page-scroll-lock {
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-page {
    height: calc(100dvh - 78px) !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  body.support-page-scroll-lock .sr-topbar {
    flex: 0 0 auto !important;
    padding: 10px 28px !important;
  }

  body.support-page-scroll-lock .sr-shell {
    --support-panel-height: calc(100dvh - 185px);

    flex: 1 1 auto !important;
    min-height: 0 !important;
    width: min(1180px, calc(100% - 42px)) !important;
    margin: 12px auto 8px !important;

    display: grid !important;
    grid-template-columns: 396px minmax(0, 1fr) !important;
    gap: 22px !important;
    align-items: stretch !important;
  }

  /* LEFT CARD */
  body.support-page-scroll-lock .sr-side-panel {
    height: var(--support-panel-height) !important;
    min-height: var(--support-panel-height) !important;
    max-height: var(--support-panel-height) !important;

    padding: 20px 20px 18px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-start !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-side-kicker {
    margin-bottom: 16px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 18px !important;
    line-height: 1.22 !important;
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    margin: 0 0 14px !important;
    font-size: 12.5px !important;
    line-height: 1.45 !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    display: grid !important;
    gap: 10px !important;
    margin: 0 !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 62px !important;
    padding: 12px 14px !important;
    align-items: center !important;
  }

  body.support-page-scroll-lock .sr-help-icon {
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
    min-height: 38px !important;
  }

  body.support-page-scroll-lock .sr-help-item strong {
    font-size: 13px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-help-item > div span {
    font-size: 11.5px !important;
    line-height: 1.35 !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    margin-top: auto !important;
    padding: 16px !important;
    border-radius: 20px !important;
    flex-shrink: 0 !important;
  }

  body.support-page-scroll-lock .sr-sla-title {
    font-size: 11px !important;
    margin-bottom: 6px !important;
  }

  body.support-page-scroll-lock .sr-sla-value {
    font-size: 18px !important;
    margin-top: 0 !important;
  }

  body.support-page-scroll-lock .sr-sla-desc {
    font-size: 12px !important;
    line-height: 1.4 !important;
    margin-top: 6px !important;
  }

  /* RIGHT SIDE */
  body.support-page-scroll-lock .sr-main-panel {
    height: var(--support-panel-height) !important;
    min-height: var(--support-panel-height) !important;
    max-height: var(--support-panel-height) !important;

    display: flex !important;
    flex-direction: column !important;
    min-width: 0 !important;
    overflow: hidden !important;
  }

  body.support-page-scroll-lock .sr-progress-card {
    flex: 0 0 auto !important;
    padding: 12px 20px !important;
    margin-bottom: 12px !important;
  }

  body.support-page-scroll-lock .sr-request-card {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: 0 !important;

    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  /* IMPORTANT: body scrolls inside card, footer stays visible */
  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding: 20px 28px 14px !important;
    scrollbar-width: thin;
  }

  body.support-page-scroll-lock .sr-card-footer {
    flex: 0 0 auto !important;
    padding: 12px 28px !important;
    background: #ffffff !important;
    border-top: 1px solid #e8edf5 !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__heading {
    font-size: 18px !important;
    line-height: 1.2 !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__sub {
    font-size: 12.5px !important;
    line-height: 1.4 !important;
  }

  body.support-page-scroll-lock .wiz-field {
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .wiz-label {
    margin-bottom: 5px !important;
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-input,
  body.support-page-scroll-lock .sr-request-card .wiz-select,
  body.support-page-scroll-lock .wiz-phone-container input {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 13px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-textarea {
    min-height: 130px !important;
    height: 130px !important;
    resize: none !important;
  }

  body.support-page-scroll-lock .sr-file-zone {
    min-height: 86px !important;
    padding: 14px !important;
  }

  body.support-page-scroll-lock .sr-priority-grid {
    gap: 8px !important;
  }

  body.support-page-scroll-lock .sr-priority-card {
    min-height: 46px !important;
    padding: 9px 12px !important;
  }

  body.support-page-scroll-lock .sr-priority-desc {
    margin-top: 8px !important;
    padding: 8px 12px !important;
    font-size: 12px !important;
  }

  body.support-page-scroll-lock .sr-review-box {
    padding: 10px 14px !important;
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-review-box .wiz-review__row {
    padding: 6px 0 !important;
  }

  body.support-page-scroll-lock .sr-autofill {
    padding: 9px 12px !important;
    margin-bottom: 10px !important;
    font-size: 12px !important;
  }
}

/* Short laptop / 150% zoom fit */
@media (min-width: 993px) and (max-height: 860px) {
  body.support-page-scroll-lock .sr-shell {
    --support-panel-height: calc(100dvh - 170px);
    grid-template-columns: 370px minmax(0, 1fr) !important;
    gap: 18px !important;
  }

  body.support-page-scroll-lock .sr-topbar {
    padding: 8px 24px !important;
  }

  body.support-page-scroll-lock .sr-page-subtitle {
    font-size: 11.5px !important;
  }

  body.support-page-scroll-lock .sr-side-panel {
    padding: 18px !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 17px !important;
    margin-bottom: 8px !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    font-size: 12px !important;
    line-height: 1.38 !important;
    margin-bottom: 12px !important;
  }

  body.support-page-scroll-lock .sr-help-list {
    gap: 8px !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 56px !important;
    padding: 10px 12px !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    padding: 14px !important;
  }

  body.support-page-scroll-lock .sr-progress-card {
    padding: 10px 16px !important;
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    padding: 16px 22px 10px !important;
  }

  body.support-page-scroll-lock .sr-card-footer {
    padding: 10px 22px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-textarea {
    min-height: 108px !important;
    height: 108px !important;
  }

  body.support-page-scroll-lock .sr-file-zone {
    min-height: 74px !important;
    padding: 12px !important;
  }

  body.support-page-scroll-lock .sr-review-box {
    padding: 8px 12px !important;
  }

  body.support-page-scroll-lock .sr-autofill {
    padding: 8px 10px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-btn {
    min-height: 38px !important;
    padding: 8px 16px !important;
  }
}

/* Very short screens */
@media (min-width: 993px) and (max-height: 740px) {
  body.support-page-scroll-lock .sr-shell {
    --support-panel-height: calc(100dvh - 158px);
    grid-template-columns: 350px minmax(0, 1fr) !important;
  }

  body.support-page-scroll-lock .sr-page-subtitle {
    display: none !important;
  }

  body.support-page-scroll-lock .sr-side-title {
    font-size: 16px !important;
  }

  body.support-page-scroll-lock .sr-side-text {
    font-size: 11px !important;
    margin-bottom: 10px !important;
  }

  body.support-page-scroll-lock .sr-help-item {
    min-height: 50px !important;
    padding: 8px 10px !important;
  }

  body.support-page-scroll-lock .sr-help-icon {
    width: 34px !important;
    height: 34px !important;
    min-width: 34px !important;
    min-height: 34px !important;
  }

  body.support-page-scroll-lock .sr-sla-card {
    padding: 12px !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    padding: 14px 18px 8px !important;
  }

  body.support-page-scroll-lock .sr-card-footer {
    padding: 8px 18px !important;
  }
}

/* Tablet and mobile */
@media (max-width: 992px) {
  body.support-page-scroll-lock {
    overflow: auto !important;
  }

  body.support-page-scroll-lock .sr-page {
    height: auto !important;
    min-height: calc(100dvh - 78px) !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  body.support-page-scroll-lock .sr-shell {
    align-items: start !important;
  }

  body.support-page-scroll-lock .sr-side-panel,
  body.support-page-scroll-lock .sr-main-panel,
  body.support-page-scroll-lock .sr-request-card {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    overflow: visible !important;
  }
}

/* =========================================================
   FINAL SMALL FIX
   1. Right step card full width
   2. Blue scrollbar
   ========================================================= */

@media (min-width: 993px) {
  body.support-page-scroll-lock .sr-progress-card,
  body.support-page-scroll-lock .sr-request-card {
    width: 100% !important;
    max-width: none !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  body.support-page-scroll-lock .sr-main-panel {
    width: 100% !important;
  }

  body.support-page-scroll-lock .sr-request-card {
    align-self: stretch !important;
  }

  /* Blue scrollbar inside step card */
  body.support-page-scroll-lock .sr-request-card .wiz-card__body {
    scrollbar-width: thin;
    scrollbar-color: #035EA5 #E6EEF8;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body::-webkit-scrollbar {
    width: 10px;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body::-webkit-scrollbar-track {
    background: #E6EEF8;
    border-radius: 999px;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body::-webkit-scrollbar-thumb {
    background: #035EA5;
    border-radius: 999px;
    border: 2px solid #E6EEF8;
  }

  body.support-page-scroll-lock .sr-request-card .wiz-card__body::-webkit-scrollbar-thumb:hover {
    background: #024A82;
  }
}

/* =========================================================
   FINAL FIX: Remove grey corner behind support request card
   Only fixes card corner/shadow/background
   No layout or functionality change
   ========================================================= */

.sr-request-card,
body.support-page-scroll-lock .sr-request-card {
  background: #ffffff !important;
  border-radius: 14px !important;
  box-shadow: none !important;
  overflow: hidden !important;
  isolation: isolate !important;
}

/* Keep body white so footer/card corner does not show grey patch */
.sr-request-card .wiz-card__body,
body.support-page-scroll-lock .sr-request-card .wiz-card__body {
  background: #ffffff !important;
}

/* Remove grey footer background inside the card */
.sr-card-footer,
body.support-page-scroll-lock .sr-card-footer {
  background: #ffffff !important;
  border-top: 1px solid #e2e8f0 !important;
  border-radius: 0 0 14px 14px !important;
}

/* Remove any old shadow/grey layer from base wizard card */
.wiz-card.sr-request-card {
  box-shadow: none !important;
}

/* Keep main panel transparent so no extra rectangle appears behind card */
.sr-main-panel,
body.support-page-scroll-lock .sr-main-panel {
  background: transparent !important;
}

/* Optional: keep only a clean border for card visibility */
.sr-request-card {
  border: 1px solid #dbe3ef !important;
}

.wiz-file-list {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.wiz-file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #dbe5ef;
  border-radius: 8px;
  background: #fff;
  color: #024a82;
}

.wiz-file-item__meta {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}

.wiz-file-item__meta strong {
  overflow: hidden;
  color: #374151;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wiz-file-item__meta span {
  color: #9ca3af;
  font-size: 11.5px;
}
  
`;

export default Support;
